import { useState, useEffect } from 'react'
import { Routes, Route, NavLink, useNavigate, useSearchParams, useParams, Link, Navigate } from 'react-router-dom'
import { FiCalendar, FiHome, FiTag, FiBarChart2, FiPlus, FiGrid, FiHelpCircle, FiShoppingCart, FiUsers, FiTrash2, FiEdit2, FiMail, FiEye, FiChevronDown, FiChevronUp, FiX, FiRefreshCw, FiCreditCard, FiLock, FiUnlock } from 'react-icons/fi'
import toast from 'react-hot-toast'
import useAuthStore from '../../stores/authStore'
import CashierDesk from './CashierDesk'
import {
    adminGetEvents,
    adminGetEvent,
    adminGetHalls,
    adminGetHall,
    adminCreateHall,
    adminUpdateHall,
    adminGenerateHallLayout,
    adminUpdateSeats,
    adminGetSectors,
    adminGetSector,
    adminCreateSector,
    adminUpdateSector,
    adminDeleteSector,
    adminUpdateSectorSeats,
    adminGenerateSectorLayout,
    adminGetPromoCodes,
    adminCreatePromoCode,
    adminUpdatePromoCode,
    adminDeletePromoCode,
    adminGetSalesAnalytics,
    adminCreateEvent,
    adminUpdateEvent,
    adminGetFaqs,
    adminCreateFaqCategory,
    adminUpdateFaqCategory,
    adminDeleteFaqCategory,
    adminCreateFaq,
    adminUpdateFaq,
    adminDeleteFaq,
    adminGetContactMessages,
    adminUpdateContactMessage,
    adminDeleteContactMessage,
    adminGetContactMessageTags,
    adminCreateContactMessageTag,
    adminUpdateContactMessageTag,
    adminDeleteContactMessageTag,
    adminGetOrders,
    adminGetUsers,
    adminGetRoles,
    adminUpdateUser,
    adminGetReportOverview,
    adminGetRevenueByHall,
    adminGetRevenueByEvent,
    adminGetRevenueByCategory,
    adminGetPerformances,
    adminCreatePerformance,
    adminUpdatePerformance,
    adminDeletePerformance,
    adminGetNewsletters,
    adminUpdateNewsletter,
    adminToggleHallActive,
    adminToggleUserActive,
} from '../../services/api'
import { getImageUrl } from '../../utils/imageUrl'
import { getCategoryLabel, categoryList } from '../../helpers/category'


const formatDateTime = (dateStr) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    const pad = (n) => n.toString().padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function AdminNav() {
    const user = useAuthStore(state => state.user)
    const isCashier = user?.role?.name === 'cashier'

    let navItems = []

    if (isCashier) {
        navItems = [
            { to: '/admin/cashier', icon: FiCreditCard, label: `${window.t('kasa')}`, exact: true },
        ]
    } else {
        navItems = [
            { to: '/admin', icon: FiBarChart2, label: `${window.t('tablo')}`, exact: true },
            { to: '/admin/cashier', icon: FiCreditCard, label: `${window.t('kasa')}` },
            { to: '/admin/events', icon: FiCalendar, label: `${window.t('sabitiya')}` },
            { to: '/admin/halls', icon: FiHome, label: `${window.t('zali')}` },
            { to: '/admin/promo-codes', icon: FiTag, label: `${window.t('promo')}` },
            { to: '/admin/help', icon: FiHelpCircle, label: `${window.t('pomoshtna')}` },
            { to: '/admin/purchases', icon: FiShoppingCart, label: `${window.t('pokupki')}` },
            { to: '/admin/users', icon: FiUsers, label: `${window.t('potrebiteli')}` },
            { to: '/admin/newsletters', icon: FiMail, label: `${window.t('byuletini')}` },
            { to: '/admin/reports', icon: FiGrid, label: `${window.t('spravki')}` },
        ]
    }

    return (
        <nav className="bg-gray-900 border-r border-gray-800 w-64 min-h-screen pt-20 px-4">
            <div className="space-y-2">
                {navItems.map(item => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.exact}
                        className={({ isActive }) => `
              flex items-center gap-3 px-4 py-3 rounded-xl transition-all
              ${isActive ? 'bg-primary-500/20 text-primary-400' : 'text-gray-400 hover:bg-gray-800'}
            `}
                    >
                        <item.icon className="w-5 h-5" />
                        {item.label}
                    </NavLink>
                ))}
            </div>

            {/* 
            <div className="mt-8 border-t border-gray-800 pt-4">
                <span className="text-gray-500 text-xs uppercase tracking-wider mb-2 block font-medium">Език / Language</span>
                <select
                    className="w-full bg-gray-800 text-gray-300 border border-gray-700 rounded-lg px-3 py-2 text-sm outline-none cursor-pointer hover:text-white"
                    value={localStorage.getItem('app_lang') || 'bg'}
                    onChange={(e) => {
                        localStorage.setItem('app_lang', e.target.value);
                        window.location.reload();
                    }}
                >
                    <option value="bg">Български (БГ)</option>
                    <option value="en">English (EN)</option>
                </select>
            </div> 
            */}
        </nav>
    )
}

function DashboardStats() {
    const [analytics, setAnalytics] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo(0, 0)
        }, 0);
    }, [])

    useEffect(() => {
        loadAnalytics()
    }, [])

    const loadAnalytics = async () => {
        try {
            const res = await adminGetSalesAnalytics({})
            setAnalytics(res.data)
        } catch (error) {
            // console.error('Грешка при зареждане на статистиките')
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return <div className="animate-pulse">{window.t('zarezhdane')}</div>
    }

    const stats = [
        { label: `${window.t('obshto_porachki')}`, value: analytics?.summary?.total_orders || 0 },
        { label: `${window.t('obshto_prihodi')}`, value: `€${analytics?.summary?.total_revenue?.toLocaleString() || 0}` },
        { label: `${window.t('prodadeni_bileti')}`, value: analytics?.summary?.total_tickets || 0 },
        { label: `${window.t('sredna_stoynost_na_porachka')}`, value: `€${analytics?.summary?.average_order_value || 0}` },
    ]

    return (
        <div>
            <h1 className="text-2xl font-bold text-white mb-6">{window.t('tablo')}</h1>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {stats.map(stat => (
                    <div key={stat.label} className="card p-6">
                        <div className="text-gray-400 text-sm">{stat.label}</div>
                        <div className="text-2xl font-bold text-white mt-1">{stat.value}</div>
                    </div>
                ))}
            </div>

            <div className="card p-6">
                <h2 className="text-lg font-bold text-white mb-4">{window.t('posledni_prodazhbi')}</h2>
                <div className="space-y-3">
                    {analytics?.daily?.slice(-7).reverse().map(day => (
                        <div key={day.date} className="flex justify-between items-center py-2 border-b border-gray-800">
                            <span className="text-gray-400">{day.date}</span>
                            <div className="flex gap-4">
                                <span className="text-gray-300">{day.orders_count} {window.t('porachki')}</span>
                                <span className="text-primary-400 font-medium">€{day.total_revenue}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

function EventsList() {
    const [events, setEvents] = useState([])
    const [halls, setHalls] = useState([])
    const [loading, setLoading] = useState(true)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedEvent, setSelectedEvent] = useState(null)
    const [searchParams, setSearchParams] = useSearchParams()

    const [page, setPage] = useState(1)
    const [lastPage, setLastPage] = useState(1)

    const [formData, setFormData] = useState({
        title: '',
        title_en: '',
        description: '',
        description_en: '',
        category: 'cinema',
        hall_id: '',
        image: null,
        thumbnail: null,
        image_url: '',
        thumbnail_url: '',
        duration_minutes: '',
        age_restriction: '',
        is_featured: false,
        is_active: true
    })

    const currentLanguage = localStorage.getItem('app_lang')

    useEffect(() => {
        loadHalls()
    }, [])

    useEffect(() => {
        window.scrollTo(0, 0)
        loadEvents()
    }, [page])

    const loadHalls = async () => {
        try {
            const res = await adminGetHalls()
            setHalls(res.data)
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        }
    }

    useEffect(() => {
        const editId = searchParams.get('edit')
        if (editId && events.length > 0) {
            const ev = events.find(e => e.id.toString() === editId)
            if (ev) {
                openModal(ev, true)
            }
        } else if (searchParams.get('new')) {
            openModal(null, true)
        }
    }, [searchParams, events])

    const loadEvents = async () => {
        try {
            const res = await adminGetEvents({ page: page })

            const data = res.data

            setEvents(data.data)
            setLastPage(data.last_page)
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        } finally {
            setLoading(false)
        }
    }

    const openModal = (event = null, fromUrl = false) => {
        if (event) {
            setSelectedEvent(event)
            setFormData({
                title: event.title || '',
                title_en: event.title_en || '',
                description: event.description || '',
                description_en: event.description_en || '',
                category: event.category || 'cinema',
                hall_id: event.hall_id || (halls.length > 0 ? halls[0].id : ''),
                image: null,
                thumbnail: null,
                image_url: event.image_url || '',
                thumbnail_url: event.thumbnail_url || '',
                duration_minutes: event.duration_minutes || '',
                age_restriction: event.age_restriction || '',
                is_featured: event.is_featured || false,
                is_active: event.is_active !== undefined ? event.is_active : true
            })
            if (!fromUrl) setSearchParams({ edit: event.id })
        } else {
            setSelectedEvent(null)
            setFormData({
                title: '',
                title_en: '',
                description: '',
                description_en: '',
                category: 'cinema',
                hall_id: halls.length > 0 ? halls[0].id : '',
                image: null,
                thumbnail: null,
                image_url: '',
                thumbnail_url: '',
                duration_minutes: '',
                age_restriction: '',
                is_featured: false,
                is_active: true
            })
            if (!fromUrl) setSearchParams({ new: 'true' })
        }
        setIsModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
        setSearchParams({})
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            const payload = new FormData()
            payload.append('title', formData.title || formData.title_en)
            payload.append('title_en', formData.title_en || formData.title)
            if (formData.description || formData.description_en) payload.append('description', formData.description || formData.description_en)
            if (formData.description_en || formData.description) payload.append('description_en', formData.description_en || formData.description)
            payload.append('category', formData.category)
            payload.append('hall_id', formData.hall_id)
            if (formData.duration_minutes) payload.append('duration_minutes', parseInt(formData.duration_minutes, 10))
            if (formData.age_restriction) payload.append('age_restriction', formData.age_restriction)

            payload.append('is_active', formData.is_active ? 1 : 0)
            payload.append('is_featured', formData.is_featured ? 1 : 0)

            if (formData.image) payload.append('image', formData.image)
            if (formData.thumbnail) payload.append('thumbnail', formData.thumbnail)

            if (selectedEvent) {
                payload.append('_method', 'PUT')
                await adminUpdateEvent(selectedEvent.slug, payload)
                toast.success(window.t('uspeshna_aktualizatsiya'))
            } else {
                await adminCreateEvent(payload)
                toast.success(window.t('uspeshno_dobavyane'))
            }
            closeModal()
            loadEvents()
        } catch (error) {
            const msgs = error.response?.data?.errors
                ? Object.values(error.response.data.errors).flat().join('\n')
                : error.response?.data?.message || window.t('zapazvaneto_na_sabitieto_ne_be_uspeshno')
            toast.error(msgs)
        }
    }


    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-white">{window.t('sabitiya')}</h1>
                <button onClick={() => openModal()} className="btn-primary">
                    <FiPlus className="mr-2" /> {window.t('dobavi_sabitie')}
                </button>
            </div>

            {loading ? (
                <div className="animate-pulse">{window.t('zarezhdane')}</div>
            ) : (
                <>
                    <div className="card overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-gray-800">
                                <tr>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('sabitie')}</th>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('kategoriya')}</th>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('zala')}</th>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('predstavleniya')}</th>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('status')}</th>
                                    <th className="text-right p-4 text-gray-400 font-medium">{window.t('deystviya')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map(event => (
                                    <tr key={event.id} className="border-t border-gray-800 hover:bg-gray-800/50">
                                        <td className="p-4">
                                            <div className="font-medium text-white">{currentLanguage === 'en' ? (event.title_en || event.title) : event.title}</div>
                                        </td>
                                        <td className="p-4 capitalize text-gray-400">{getCategoryLabel(event.category)}</td>
                                        <td className="p-4 text-gray-400">{event.hall ? event.hall.name : '-'}</td>
                                        <td className="p-4 text-gray-400">{event.performances_count || 0}</td>
                                        <td className="p-4">
                                            <span className={`px-2 pt-[3px] pb-[5px] rounded-full text-xs ${event.is_active
                                                ? 'bg-green-500/20 text-green-800'
                                                : 'bg-red-500/20 text-red-800'
                                                }`}>
                                                {event.is_active ? `${window.t('aktiven')}` : `${window.t('neaktiven')}`}
                                            </span>
                                        </td>
                                        <td className="p-4 text-right flex justify-end gap-3">
                                            <Link to={`/admin/events/${event.slug}/performances`} className="btn-secondary py-1 px-3 pt-[3px] pb-[5px] text-sm">
                                                {window.t('predstavleniya')}
                                            </Link>
                                            <button onClick={() => openModal(event)} className="btn-secondary py-1 px-3 pt-[3px] pb-[5px] text-sm bg-blue-200">{window.t('redaktiray')}</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-8 text-center">
                        <select
                            value={page}
                            onChange={(e) => setPage(Number(e.target.value))}
                            className="bg-gray-800 text-gray-300 border border-gray-700 rounded-lg px-3 py-2 text-sm outline-none cursor-pointer hover:text-white"
                        >
                            {Array.from({ length: lastPage }, (_, i) => i + 1).map((p) => (
                                <option key={p} value={p}>
                                    {p}
                                </option>
                            ))}
                        </select>
                    </div>
                </>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 pt-20 overflow-y-auto">
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-2xl my-8 relative max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-white">
                                {selectedEvent ? `${window.t('redaktiray_sabitie')}` : `${window.t('dobavi_sabitie')}`}
                            </h2>
                            <button onClick={closeModal} className="text-gray-400 hover:text-white transition-colors">
                                <FiX className="w-6 h-6" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label">{window.t('zaglavie')}</label>
                                    <input
                                        type="text"
                                        value={currentLanguage === 'en' ? formData.title_en : formData.title}
                                        onChange={e => {
                                            if (currentLanguage === 'en') {
                                                setFormData({ ...formData, title_en: e.target.value })
                                            } else {
                                                setFormData({ ...formData, title: e.target.value })
                                            }
                                        }}
                                        className="input"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="label">{window.t('kategoriya')}</label>
                                    <select
                                        value={formData.category}
                                        onChange={e => setFormData({ ...formData, category: e.target.value })}
                                        className="input"
                                    >
                                        {categoryList.map((x) => (
                                            <option key={x.value} value={x.value}>
                                                {window.t(x.text)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="label">{window.t('zala')}</label>
                                <select
                                    value={formData.hall_id}
                                    onChange={e => setFormData({ ...formData, hall_id: e.target.value })}
                                    className="input"
                                    required
                                >
                                    <option value="">{window.t('izberi_zala')}</option>
                                    {halls.map(hall => (
                                        <option key={hall.id} value={hall.id}>
                                            {hall.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="label">{window.t('opisanie')}</label>
                                <textarea
                                    value={currentLanguage === 'en' ? formData.description_en : formData.description}
                                    onChange={e => {
                                        if (currentLanguage === 'en') {
                                            setFormData({ ...formData, description_en: e.target.value })
                                        } else {
                                            setFormData({ ...formData, description: e.target.value })
                                        }
                                    }}
                                    className="input h-24 resize-none"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label">{window.t('osnovna_snimka')}</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={e => setFormData({ ...formData, image: e.target.files[0] })}
                                        className="input bg-gray-800"
                                    />
                                    {selectedEvent?.image_url && <img src={getImageUrl(selectedEvent.image_url)} alt="Cover" className="mt-4 h-32 rounded-lg object-contain border border-gray-700 bg-gray-800" />}
                                </div>
                                <div>
                                    <label className="label">{window.t('malka_snimka')}</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={e => setFormData({ ...formData, thumbnail: e.target.files[0] })}
                                        className="input bg-gray-800"
                                    />
                                    {selectedEvent?.thumbnail_url && <img src={getImageUrl(selectedEvent.thumbnail_url)} alt="Thumbnail" className="mt-4 h-32 rounded-lg object-contain border border-gray-700 bg-gray-800" />}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label">{window.t('prod_minuti')}</label>
                                    <input
                                        type="number"
                                        value={formData.duration_minutes}
                                        onChange={e => setFormData({ ...formData, duration_minutes: e.target.value })}
                                        className="input"
                                    />
                                </div>
                                <div>
                                    <label className="label">{window.t('vazrastovo_ogranichenie')}</label>
                                    <input
                                        type="text"
                                        placeholder="18+, PG-13"
                                        value={formData.age_restriction}
                                        onChange={e => setFormData({ ...formData, age_restriction: e.target.value })}
                                        className="input"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-6 mt-6">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_featured}
                                        onChange={e => setFormData({ ...formData, is_featured: e.target.checked })}
                                        className="form-checkbox text-primary-500 rounded bg-gray-800 border-gray-700 focus:ring-primary-500 focus:ring-offset-gray-900"
                                    />
                                    <span className="text-gray-300">{window.t('preporachano_sabitie')}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                                        className="form-checkbox text-primary-500 rounded bg-gray-800 border-gray-700 focus:ring-primary-500 focus:ring-offset-gray-900"
                                    />
                                    <span className="text-gray-300">{window.t('aktiven')}</span>
                                </label>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-800 mt-6 md:sticky bottom-0 bg-gray-900 pb-2">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="btn-secondary"
                                >
                                    {window.t('otkaz')}
                                </button>
                                <button type="submit" className="btn-primary">
                                    {selectedEvent ? `${window.t('obnovi')}` : `${window.t('sazdai')}`}
                                </button>
                            </div>
                        </form>
                    </div>
                </div >
            )
            }
        </div >
    )
}

function HallsList() {
    const navigate = useNavigate()
    const [halls, setHalls] = useState([])
    const [loading, setLoading] = useState(true)

    // Add/Edit Hall Form State
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedHall, setSelectedHall] = useState(null)
    const [formData, setFormData] = useState({
        name: '',
        name_en: '',
        type: 'seated',
        total_capacity: 100,
        description: '',
        description_en: ''
    })
    const [existingImages, setExistingImages] = useState([])
    const [images, setImages] = useState([])

    const currentLanguage = localStorage.getItem('app_lang')

    const openCreateModal = () => {
        setFormData({ name: '', name_en: '', type: 'seated', total_capacity: 100, description: '', description_en: '' })
        setExistingImages([])
        setImages([])
        setSelectedHall(null)
        setIsModalOpen(true)
    }

    const openEditModal = (hall) => {
        setFormData({
            name: hall.name,
            name_en: hall.name_en || '',
            type: hall.type,
            total_capacity: hall.total_capacity,
            description: hall.description || '',
            description_en: hall.description_en || ''
        })
        setExistingImages(hall.images || [])
        setImages([])
        setSelectedHall(hall)
        setIsModalOpen(true)
    }

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo(0, 0)
        }, 0);
    }, [])

    useEffect(() => {
        loadHalls()
    }, [])

    const loadHalls = async () => {
        try {
            const res = await adminGetHalls()
            setHalls(res.data)
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        } finally {
            setLoading(false)
        }
    }

    const handleSubmitHall = async (e) => {
        e.preventDefault()
        try {
            const fd = new FormData()
            fd.append('name', formData.name || formData.name_en)
            fd.append('name_en', formData.name_en || formData.name)
            fd.append('type', formData.type)
            fd.append('total_capacity', parseInt(formData.total_capacity) || 0)
            if (formData.description || formData.description_en) fd.append('description', formData.description || formData.description_en)
            if (formData.description_en || formData.description) fd.append('description_en', formData.description_en || formData.description)

            existingImages.forEach(img => {
                fd.append('existing_images[]', img)
            })

            Array.from(images).forEach(file => {
                fd.append('images[]', file)
            })

            if (selectedHall) {
                await adminUpdateHall(selectedHall.id, fd)
                toast.success(window.t('uspeshna_aktualizatsiya'))
            } else {
                await adminCreateHall(fd)
                toast.success(window.t('uspeshno_dobavyane'))
            }

            setIsModalOpen(false)
            setFormData({ name: '', name_en: '', type: 'seated', total_capacity: 100, description: '', description_en: '' })
            setImages([])
            setExistingImages([])
            loadHalls()
        } catch (error) {
            toast.error(error.response?.data?.message || window.t('neuspeshno_zapazvane_na_zalata'))
        }
    }

    const removeExistingImage = (idx) => {
        setExistingImages(prev => prev.filter((_, i) => i !== idx))
    }

    const removeNewImage = (idx) => {
        const dt = new DataTransfer()
        const files = Array.from(images)
        files.filter((_, i) => i !== idx).forEach(f => dt.items.add(f))
        setImages(dt.files)
    }

    const handleImageDrop = (e) => {
        const newFiles = Array.from(e.target.files)
        if (existingImages.length + images.length + newFiles.length > 5) {
            toast.error(window.t('pozvoleni_sa_maksimum_5_snimki'))
            return
        }

        const dt = new DataTransfer()
        Array.from(images).forEach(f => dt.items.add(f))
        newFiles.forEach(f => dt.items.add(f))
        setImages(dt.files)
    }

    // Helper url
    const getHallImgUrl = (path) => {
        if (!path) return ''
        if (path.startsWith('http')) return path
        const base = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000'
        return `${base}${path}`
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-white">{window.t('zali')}</h1>
                <button
                    onClick={openCreateModal}
                    className="btn-primary"
                >
                    <FiPlus className="mr-2" /> {window.t('dobavi_zala')}
                </button>
            </div>

            {loading ? (
                <div className="animate-pulse">{window.t('zarezhdane')}</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {halls.map(hall => (
                        <div key={hall.id} className={`card p-6 ${!hall.is_active ? 'opacity-60 border-red-500/30' : ''}`}>
                            <div className="flex items-center gap-3 mb-4">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${hall.is_active ? 'bg-primary-500/20' : 'bg-red-500/20'}`}>
                                    <FiGrid className={hall.is_active ? 'text-primary-400' : 'text-red-400'} />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-bold text-white">{currentLanguage === 'en' ? (hall.name_en || hall.name) : hall.name}</h3>
                                    <p className="text-gray-400 text-sm capitalize">
                                        {hall.type === 'seated' ? `${window.t('sedyashti_mesta')}` : hall.type === 'general_admission' ? `${window.t('pravostoyashti')}` : hall.type?.replace('_', ' ')}
                                    </p>
                                </div>
                                <span className={`px-2 pt-[3px] pb-[5px] rounded-full text-xs ${hall.is_active ? 'bg-green-500/20 text-green-800' : 'bg-red-500/20 text-red-800'}`}>
                                    {hall.is_active ? `${window.t('aktivna')}` : `${window.t('skrita')}`}
                                </span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-400">{window.t('kapatsitet')}</span>
                                <span className="text-white">{hall.total_capacity} {window.t('mesta')}</span>
                            </div>
                            <div className="flex gap-2 w-full mt-4">
                                <button onClick={() => openEditModal(hall)} className="btn-secondary flex-1 text-sm py-2">
                                    {window.t('redaktiray_info')}
                                </button>
                                <button onClick={() => navigate(`/admin/halls/${hall.id}/map`)} className="btn-outline flex-1 text-sm py-2">
                                    {window.t('shema_na_zalata')}
                                </button>
                            </div>
                            <button
                                onClick={async () => {
                                    try {
                                        await adminToggleHallActive(hall.id)
                                        toast.success(hall.is_active ? window.t('zalata_e_skrитa_ot_publichnata_chast') : window.t('zalata_e_vidima_v_publichnata_chast'))
                                        loadHalls()
                                    } catch (error) {
                                        toast.error(window.t('greshka_pri_aktualizirane_na_dannite'))
                                    }
                                }}
                                className={`w-full mt-2 py-2 px-3 text-sm rounded-lg border transition-colors flex items-center justify-center gap-2 ${hall.is_active
                                    ? 'border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50'
                                    : 'border-green-500/30 text-green-400 hover:bg-green-500/10 hover:border-green-500/50'
                                    }`}
                            >
                                <FiEye className="w-4 h-4" />
                                {hall.is_active ? window.t('skriy_ot_publichnata_chast') : window.t('pokaji_v_publichnata_chast')}
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Add/Edit Hall Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
                        <div className="flex justify-between items-center p-6 border-b border-gray-800">
                            <h2 className="text-xl font-bold text-white">
                                {selectedHall ? `${window.t('obnovi')}` : `${window.t('sazdai')}`}
                            </h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white">
                                <FiX size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmitHall} className="p-6 space-y-4">
                            <div>
                                <label className="label">{window.t('ime_na_zala')}</label>
                                <input
                                    type="text"
                                    required
                                    value={currentLanguage === 'en' ? formData.name_en : formData.name}
                                    onChange={e => {
                                        if (currentLanguage === 'en') {
                                            setFormData({ ...formData, name_en: e.target.value })
                                        } else {
                                            setFormData({ ...formData, name: e.target.value })
                                        }
                                    }}
                                    className="input"
                                    placeholder={window.t('napr_kino_zala_1')}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label">{window.t('tip')}</label>
                                    <select
                                        value={formData.type}
                                        onChange={e => setFormData({ ...formData, type: e.target.value })}
                                        className="input"
                                    >
                                        <option value="seated">{window.t('sedyashti_mesta')}</option>
                                        <option value="general_admission">{window.t('pravostoyashti')}</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="label">{window.t('obsht_kapatsitet')}</label>
                                    <input
                                        type="number"
                                        required
                                        min="1"
                                        value={formData.total_capacity}
                                        onChange={e => setFormData({ ...formData, total_capacity: e.target.value })}
                                        className="input"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="label">{window.t('opisanie_po_izbor')}</label>
                                <textarea
                                    value={currentLanguage === 'en' ? formData.description_en : formData.description}
                                    onChange={e => {
                                        if (currentLanguage === 'en') {
                                            setFormData({ ...formData, description_en: e.target.value })
                                        } else {
                                            setFormData({ ...formData, description: e.target.value })
                                        }
                                    }}
                                    className="input min-h-[100px]"
                                    placeholder={window.t('za_tazi_zala')}
                                />
                            </div>

                            <div>
                                <label className="label">{window.t('galeriya_maks_5_snimki')}</label>
                                <div className="space-y-4">
                                    {(existingImages.length > 0 || images.length > 0) && (
                                        <div className="grid grid-cols-5 gap-2">
                                            {existingImages.map((img, idx) => (
                                                <div key={`ext-${idx}`} className="relative aspect-square rounded overflow-hidden">
                                                    <img src={getHallImgUrl(img)} alt="Hall" className="w-full h-full object-cover" />
                                                    <button type="button" onClick={() => removeExistingImage(idx)} className="absolute top-1 right-1 bg-black/50 hover:bg-red-500 rounded-full p-1 transition">
                                                        <FiX size={12} className="text-white" />
                                                    </button>
                                                </div>
                                            ))}
                                            {Array.from(images).map((file, idx) => (
                                                <div key={`new-${idx}`} className="relative aspect-square rounded overflow-hidden">
                                                    <img src={URL.createObjectURL(file)} alt="New Hall" className="w-full h-full object-cover opacity-75" />
                                                    <button type="button" onClick={() => removeNewImage(idx)} className="absolute top-1 right-1 bg-black/50 hover:bg-red-500 rounded-full p-1 transition">
                                                        <FiX size={12} className="text-white" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {existingImages.length + images.length < 5 && (
                                        <input
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            onChange={handleImageDrop}
                                            className="input"
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-gray-800">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="btn-secondary"
                                >
                                    {window.t('otkaz')}
                                </button>
                                <button type="submit" className="btn-primary">
                                    {selectedHall ? `${window.t('obnovi')}` : `${window.t('sazdai')}`}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}

function HallLayoutRedirect() {
    const { hallId } = useParams()
    return <Navigate to={`/admin/halls/${hallId}/map`} replace />
}

function HallMapEditor() {
    const { hallId } = useParams()
    const navigate = useNavigate()
    const [hall, setHall] = useState(null)
    const [sectors, setSectors] = useState([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [dirtyIds, setDirtyIds] = useState(new Set())
    const [dragState, setDragState] = useState(null)
    const [isAddOpen, setIsAddOpen] = useState(false)
    const [newSector, setNewSector] = useState({ name: '', name_en: '', type: 'seated', width: 300, height: 200 })

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo(0, 0)
        }, 0);
    }, [])

    useEffect(() => {
        loadData()
    }, [hallId])

    const loadData = async () => {
        try {
            const [hallRes, sectorsRes] = await Promise.all([
                adminGetHall(hallId),
                adminGetSectors(hallId),
            ])
            setHall(hallRes.data)
            setSectors(sectorsRes.data)
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        } finally {
            setLoading(false)
        }
    }
    const handleMouseDown = (e, sector) => {
        e.preventDefault()
        setDragState({
            sectorId: sector.id,
            startMouseX: e.clientX,
            startMouseY: e.clientY,
            startSectorX: sector.x,
            startSectorY: sector.y,
        })
    }
    const handleMouseMove = (e) => {
        if (!dragState) return
        const dx = e.clientX - dragState.startMouseX
        const dy = e.clientY - dragState.startMouseY
        setSectors(prev => prev.map(s => s.id === dragState.sectorId
            ? { ...s, x: Math.max(0, dragState.startSectorX + dx), y: Math.max(0, dragState.startSectorY + dy) }
            : s
        ))
    }
    const handleMouseUp = () => {
        if (dragState) {
            setDirtyIds(prev => new Set([...prev, dragState.sectorId]))
            setDragState(null)
        }
    }
    const handleSavePositions = async () => {
        setSaving(true)
        try {
            const dirty = sectors.filter(s => dirtyIds.has(s.id))
            await Promise.all(dirty.map(s => adminUpdateSector(hallId, s.id, { x: s.x, y: s.y })))
            setDirtyIds(new Set())
            toast.success(window.t('pozitsiite_sa_zapazeni'))
        } catch (error) {
            toast.error(window.t('greshka_pri_zapazvane_na_pozitsiite'))
        } finally {
            setSaving(false)
        }
    }
    const handleAddSector = async (e) => {
        e.preventDefault()
        try {
            await adminCreateSector(hallId, { ...newSector, x: 20, y: 20 })
            toast.success(window.t('uspeshno_dobavyane'))
            setIsAddOpen(false)
            setNewSector({ name: '', name_en: '', type: 'seated', width: 300, height: 200 })
            loadData()
        } catch (error) {
            toast.error(error.response?.data?.message || window.t('greshka_pri_dobavyane_na_sektor'))
        }
    }
    const handleDeleteSector = async (sector) => {
        if (!window.confirm(`${window.t('sigrni_li_ste_che_iskate_da_iztriete_sektora')} "${sector.name}"?`)) return
        try {
            await adminDeleteSector(hallId, sector.id)
            toast.success(window.t('uspeshno_iztrivane'))
            loadData()
        } catch (error) {
            toast.error(error.response?.data?.message || window.t('greshka_pri_iztrivane_na_dannite'))
        }
    }
    if (loading) return <div className="text-gray-400">{window.t('zarezhdane')}</div>
    if (!hall) return <div className="text-gray-400">{window.t('zalata_ne_e_namerena')}</div>
    const MAP_W = 900
    const MAP_H = 600
    return (
        <div>
            <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-800">
                <button onClick={() => navigate('/admin/halls')} className="text-gray-400 hover:text-white transition-colors flex items-center shrink-0">
                    &larr; {window.t('obratno_kam_zalite')}
                </button>
                <span className="text-gray-700">|</span>
                <h1 className="text-2xl font-bold text-white truncate">{hall.name}</h1>
                <span className="text-gray-500 font-medium whitespace-nowrap">
                    {window.t('redaktirane_na_shema')}
                </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-1 space-y-4">
                    <div className="card p-4">
                        <h3 className="font-bold text-white mb-3">
                            {window.t('sektori')}
                        </h3>
                        <div className="space-y-2">
                            {sectors.map(s => (
                                <div key={s.id} className="flex items-center justify-between bg-gray-800 rounded-lg p-3">
                                    <div>
                                        <p className="text-white text-sm font-medium">{s.name}</p>
                                        <p className="text-gray-400 text-xs">{s.seats_count ?? 0} {window.t('mesta')}</p>
                                    </div>
                                    <div className="flex gap-1">
                                        <button
                                            onClick={() => navigate(`/admin/halls/${hallId}/sectors/${s.id}/layout`)}
                                            className="btn-secondary text-xs px-2 py-1"
                                            title={window.t('redaktirane_na_shemata')}
                                        >
                                            <FiGrid className="w-3 h-3" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteSector(s)}
                                            className="text-red-400 hover:text-red-300 px-2 py-1"
                                            title={window.t('iztrivane')}
                                        >
                                            <FiTrash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button onClick={() => setIsAddOpen(true)} className="btn-primary w-full mt-4 text-sm">
                            <FiPlus className="mr-1" /> {window.t('dobavi_sektor')}
                        </button>
                        {dirtyIds.size > 0 && (
                            <button onClick={handleSavePositions} disabled={saving} className="btn-secondary w-full mt-2 text-sm bg-green-300 hover:bg-green-200">
                                {saving ? `${window.t('zapazvane')}` : `${window.t('zapazi_pozitsii')}`}
                            </button>
                        )}
                    </div>
                    <div className="card p-4 text-sm text-gray-400">
                        <p className="font-medium text-white mb-1">{window.t('instruktsii')}</p>
                        <p>{window.t('kliknete_varhu_sektor_za_da_redaktirate')}</p>
                    </div>
                </div>
                <div className="lg:col-span-3">
                    <div className="card overflow-auto">
                        <div
                            className="relative bg-gray-950 border border-gray-800 rounded-xl select-none"
                            style={{ width: MAP_W + 'px', height: MAP_H + 'px', cursor: dragState ? 'grabbing' : 'default' }}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                        >
                            <div className="absolute top-4 left-1/2 -translate-x-1/2 w-2/3 h-10 border-2 border-dashed border-gray-700 rounded-t-[100px] flex items-center justify-center text-gray-500 font-bold uppercase tracking-widest text-xs">
                                {window.t('scena_ekran')}
                            </div>
                            {sectors.map(s => (
                                <div
                                    key={s.id}
                                    className={`absolute border-2 rounded-lg flex flex-col items-center justify-center cursor-grab transition-shadow ${dirtyIds.has(s.id) ? 'border-yellow-400 shadow-yellow-400/30 shadow-lg' : 'border-primary-500/60 hover:border-primary-400'}`}
                                    style={{
                                        left: s.x + 'px',
                                        top: s.y + 'px',
                                        width: (s.width || 200) + 'px',
                                        height: (s.height || 120) + 'px',
                                        transform: s.rotation ? `rotate(${s.rotation}deg)` : undefined,
                                        background: 'rgba(139,92,246,0.08)',
                                    }}
                                    onMouseDown={(e) => handleMouseDown(e, s)}
                                >
                                    <p className="text-white font-bold text-sm pointer-events-none">{s.name}</p>
                                    <p className="text-gray-400 text-xs pointer-events-none">{s.seats_count ?? 0} {window.t('mesta')}</p>
                                    <button
                                        className="absolute top-1 right-1 text-gray-500 hover:text-white"
                                        onMouseDown={e => e.stopPropagation()}
                                        onClick={() => navigate(`/admin/halls/${hallId}/sectors/${s.id}/layout`)}
                                        title={window.t('redaktirane_na_shemata')}
                                    >
                                        <FiEdit2 className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            {isAddOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md shadow-2xl">
                        <div className="flex justify-between items-center p-6 border-b border-gray-800">
                            <h2 className="text-xl font-bold text-white">{window.t('dobavi_sektor')}</h2>
                            <button onClick={() => setIsAddOpen(false)} className="text-gray-400 hover:text-white"><FiX size={24} /></button>
                        </div>
                        <form onSubmit={handleAddSector} className="p-6 space-y-4">
                            <div>
                                <label className="label">{window.t('ime_na_sektora')}</label>
                                <input type="text" required value={newSector.name} onChange={e => setNewSector({ ...newSector, name: e.target.value })} className="input" placeholder="Sector A" />
                            </div>
                            <div>
                                <label className="label">{window.t('tip')}</label>
                                <select value={newSector.type} onChange={e => setNewSector({ ...newSector, type: e.target.value })} className="input">
                                    <option value="seated">{window.t('sedyashti_mesta')}</option>
                                    <option value="general_admission">{window.t('pravostoyashti')}</option>
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label">{window.t('shirina')} (px)</label>
                                    <input type="number" min="100" value={newSector.width} onChange={e => setNewSector({ ...newSector, width: parseInt(e.target.value) })} className="input" />
                                </div>
                                <div>
                                    <label className="label">{window.t('visochina')} (px)</label>
                                    <input type="number" min="100" value={newSector.height} onChange={e => setNewSector({ ...newSector, height: parseInt(e.target.value) })} className="input" />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-6 border-t border-gray-800">
                                <button type="button" onClick={() => setIsAddOpen(false)} className="btn-secondary">{window.t('otkaz')}</button>
                                <button type="submit" className="btn-primary">{window.t('dobavi_sektor')}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}

function SectorLayoutEditor() {
    const { hallId, sectorId } = useParams();
    const navigate = useNavigate();
    const [hall, setHall] = useState(null);
    const [sector, setSector] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState(10);
    const [cols, setCols] = useState(10);
    const [gapAfterRow, setGapAfterRow] = useState('');
    const [generating, setGenerating] = useState(false);

    // State for SYMMETRICAL Sector
    const [isSymmetrical, setIsSymmetrical] = useState(false);

    // State for ROW and COL
    const [targetRow, setTargetRow] = useState('');
    const [newRowLabel, setNewRowLabel] = useState('');
    const [targetCol, setTargetCol] = useState('');
    const [newColLabel, setNewColLabel] = useState('');

    // State for SEAT
    const [targetSeatRow, setTargetSeatRow] = useState('');
    const [targetSeatNumber, setTargetSeatNumber] = useState('');
    const [newSeatLabel, setNewSeatLabel] = useState('');

    const currentLanguage = localStorage.getItem('app_lang')

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo(0, 0)
        }, 0);
    }, [])

    useEffect(() => { loadData(); }, [hallId, sectorId]);

    const loadData = async () => {
        try {
            const [hallRes, sectorRes] = await Promise.all([adminGetHall(hallId), adminGetSector(hallId, sectorId)]);
            setHall(hallRes.data);
            setSector(sectorRes.data);

            if (sectorRes.data.is_symmetrical !== undefined) {
                setIsSymmetrical(sectorRes.data.is_symmetrical);
            }

            if (hallRes.data.layout_config) {
                setRows(hallRes.data.layout_config.rows || 10);
                setCols(hallRes.data.layout_config.cols || 10);
                setGapAfterRow(hallRes.data.layout_config.gap_after_row || '');
            }
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async (e) => {
        e.preventDefault();
        try {
            setGenerating(true);
            const payload = { rows: parseInt(rows), cols: parseInt(cols) };
            if (gapAfterRow) payload.gap_after_row = parseInt(gapAfterRow);
            await adminGenerateSectorLayout(hallId, sectorId, payload);
            toast.success(window.t('generiraneto_na_shemata_e_uspeshno'));
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.message || window.t('greshka_pri_generirane_na_shemata'));
        } finally {
            setGenerating(false);
        }
    };

    const handleRenameRow = () => {
        if (!targetRow || !newRowLabel) return;
        setSector(prev => ({ ...prev, seats: prev.seats.map(s => s.row === targetRow ? { ...s, row: newRowLabel } : s) }));
        setTargetRow(''); setNewRowLabel('');
        toast.success(`${window.t('preimenuvane_na_red')} ${targetRow} -> ${newRowLabel}`);
    };

    const handleRenameCol = () => {
        if (!targetCol || !newColLabel) return;
        setSector(prev => ({ ...prev, seats: prev.seats.map(s => s.number === targetCol ? { ...s, number: newColLabel } : s) }));
        setTargetCol(''); setNewColLabel('');
        toast.success(`${window.t('preimenuvane_na_kolona')} ${targetCol} -> ${newColLabel}`);
    };

    const handleRenameSeat = () => {
        if (!targetSeatRow || !targetSeatNumber || !newSeatLabel) return;
        setSector(prev => ({
            ...prev,
            seats: prev.seats.map(s =>
                (s.row === targetSeatRow && s.number === targetSeatNumber)
                    ? { ...s, number: newSeatLabel }
                    : s
            )
        }));
        setTargetSeatRow('');
        setTargetSeatNumber('');
        setNewSeatLabel('');
        toast.success(window.t('uspeshna_aktualizatsiya'));
    };

    const toggleSeatType = (seat) => {
        const typeProgression = { 'standard': 'vip', 'vip': 'accessible', 'accessible': 'reserved', 'reserved': 'disabled', 'disabled': 'standard' };
        const nextType = typeProgression[seat.is_active ? seat.type : 'disabled'];
        setSector(prev => ({
            ...prev,
            seats: prev.seats.map(s => {
                if (s.id !== seat.id) return s;
                return nextType === 'disabled' ? { ...s, is_active: false } : { ...s, type: nextType, is_active: true };
            })
        }));
    };

    const handleSaveSeats = async () => {
        try {
            await adminUpdateSectorSeats(hallId, sectorId, sector.seats, isSymmetrical);
            toast.success(window.t('uspeshna_aktualizatsiya'));
            window.scrollTo(0, 0)
        } catch (error) {
            toast.error(window.t('greshka_pri_zapazvane_na_mestata'))
        }
    };

    if (loading) return <div className="text-gray-400">{window.t('zarezhdane')}</div>;
    if (!sector) return <div className="text-gray-400">{window.t('zalata_ne_e_namerena')}</div>;

    const getMaxBounds = () => {
        if (!sector.seats || sector.seats.length === 0) return { width: 800, height: 600 };
        const minX = Math.min(...sector.seats.map(s => s.pos_x));
        const maxX = Math.max(...sector.seats.map(s => s.pos_x));
        const maxY = Math.max(...sector.seats.map(s => s.pos_y));
        return { width: maxX + minX + 32, height: maxY + 50 };
    };

    const bounds = getMaxBounds();
    const uniqueRows = sector?.seats ? [...new Set(sector.seats.map(s => s.row))].sort() : [];
    const uniqueCols = sector?.seats ? [...new Set(sector.seats.map(s => s.number))].sort((a, b) => parseInt(a) - parseInt(b)) : [];

    return (
        <div>
            <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-800">
                <button onClick={() => navigate(`/admin/halls/${hallId}/map`)} className="text-gray-400 hover:text-white transition-colors flex items-center shrink-0">
                    &larr; {window.t('obratno_kam_zalite')}
                </button>
                <span className="text-gray-700">|</span>
                <h1 className="text-2xl font-bold text-white truncate">{hall?.name}</h1>
                <span className="text-gray-500"> &gt; </span>
                <span className="text-white font-medium truncate">{sector.name}</span>
                <span className="text-gray-500 font-medium whitespace-nowrap">{window.t('redaktiray_shema')}</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-1 space-y-6">
                    <div className="card p-6">
                        <h3 className="font-bold text-white mb-4">{window.t('generiray_shema')}</h3>
                        <form onSubmit={handleGenerate} className="space-y-4">
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">{window.t('redove')}</label>
                                <input type="number" min="1" max="100" value={rows} onChange={e => setRows(e.target.value)} className="input w-full" required />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">{window.t('koloni')}</label>
                                <input type="number" min="1" max="100" value={cols} onChange={e => setCols(e.target.value)} className="input w-full" required />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">{window.t('razstoyanie_mezhdu_redovete_po_izbor')}</label>
                                <input type="number" min="1" max="100" value={gapAfterRow} onChange={e => setGapAfterRow(e.target.value)} className="input w-full" placeholder="5" />
                            </div>
                            <button type="submit" className="btn-primary w-full" disabled={generating}>
                                {generating ? `${window.t('generirane')}` : `${window.t('generirai_shema')}`}
                            </button>
                        </form>
                    </div>

                    <div className="card p-6">
                        <h3 className="font-bold text-white mb-4">{window.t('redaktiray_etiketi')}</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">{window.t('promyana_na_ime_na_red')}</label>
                                <div className="flex gap-2">
                                    <select className="input px-2 py-2 flex-1" value={targetRow} onChange={e => setTargetRow(e.target.value)}>
                                        <option value="">{window.t('red')}</option>
                                        {uniqueRows.map(r => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                    <input type="text" className="input px-2 py-2 flex-1" placeholder={window.t('novo_ime')} value={newRowLabel} onChange={e => setNewRowLabel(e.target.value)} />
                                    <button className="btn-secondary px-3 py-2 text-sm" onClick={handleRenameRow}>{window.t('prilozhi')}</button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">{window.t('promyana_na_ime_na_kolona')}</label>
                                <div className="flex gap-2">
                                    <select className="input px-2 py-2 flex-1" value={targetCol} onChange={e => setTargetCol(e.target.value)}>
                                        <option value="">{window.t('kolona')}</option>
                                        {uniqueCols.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                    <input type="text" className="input px-2 py-2 flex-1" placeholder={window.t('novo_ime')} value={newColLabel} onChange={e => setNewColLabel(e.target.value)} />
                                    <button className="btn-secondary px-3 py-2 text-sm" onClick={handleRenameCol}>{window.t('prilozhi')}</button>
                                </div>
                            </div>

                            {/* Change to a specific seat */}
                            <div className="pt-2 border-t border-gray-700">
                                <label className="block text-sm text-gray-400 mb-1">{window.t('promyana_na_konkretno_myasto')}</label>
                                <div className="flex flex-col gap-2">
                                    <div className="flex gap-2">
                                        <select
                                            className="input px-2 py-2 flex-1"
                                            value={targetSeatRow}
                                            onChange={e => {
                                                setTargetSeatRow(e.target.value);
                                                setTargetSeatNumber(''); // Reset the seat when changing the line
                                            }}
                                        >
                                            <option value="">{window.t('red')}</option>
                                            {uniqueRows.map(r => <option key={r} value={r}>{r}</option>)}
                                        </select>
                                        <select
                                            className="input px-2 py-2 flex-1"
                                            value={targetSeatNumber}
                                            onChange={e => setTargetSeatNumber(e.target.value)}
                                            disabled={!targetSeatRow}
                                        >
                                            <option value="">{window.t('myasto_dots')}</option>
                                            {targetSeatRow && sector?.seats
                                                ?.filter(s => s.row === targetSeatRow)
                                                .sort((a, b) => parseInt(a.number) - parseInt(b.number))
                                                .map(s => (
                                                    <option key={s.id || `${s.row}-${s.number}`} value={s.number}>
                                                        {s.number}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                    </div>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            className="input px-2 py-2 flex-1"
                                            placeholder={window.t('novo_ime')}
                                            value={newSeatLabel}
                                            onChange={e => setNewSeatLabel(e.target.value)}
                                        />
                                        <button className="btn-secondary px-3 py-2 text-sm" onClick={handleRenameSeat}>
                                            {window.t('prilozhi')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {/* END - Change to a specific seat */}

                        </div>
                    </div>

                    {/* SELECT FOR SYMMETRY */}
                    <div className="card p-6">
                        <h3 className="font-bold text-white mb-4">{window.t('oformlenie_na_sektora')}</h3>
                        <div>
                            <label className="block text-sm text-gray-400 mb-1">{window.t('simetriya')}</label>
                            <select
                                className="input w-full px-2 py-2"
                                value={isSymmetrical}
                                onChange={e => setIsSymmetrical(e.target.value === 'true')}
                            >
                                <option value={false}>{window.t('standartno')}</option>
                                <option value={true}>{window.t('simetricheno')}</option>
                            </select>
                        </div>
                    </div>

                    <div className="card p-6">
                        <h3 className="font-bold text-white mb-4">{window.t('razshireni_instrumenti')}</h3>
                        <p className="text-sm text-gray-400 mb-4">
                            {currentLanguage === 'en'
                                ? <>Click on a seat to change its type: Standard &gt; VIP &gt; Disabled &gt; Reserved &gt; Hidden.<br /><br />Press “Save layout” when you are ready.</>
                                : <>Кликнете върху място, за да сменяте типа: Стандартно &gt; VIP &gt; За хора с увреждания &gt; Резервирано &gt; Скрито.<br /><br />Натиснете „Запази оформлението“, когато сте готови.</>
                            }
                        </p>
                        <button onClick={handleSaveSeats} className="btn-secondary w-full py-3 flex items-center justify-center uppercase font-bold tracking-widest text-xs bg-green-300 hover:bg-green-200">
                            {window.t('zapazi_oformlenieto')}
                        </button>
                    </div>

                    <div className="card p-4 text-sm text-gray-400">
                        <h3 className="font-medium text-white mb-2">{window.t('legend')}</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded-t-lg bg-gray-500"></div>
                                {window.t('standard')}
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded-t-lg bg-yellow-500"></div>
                                {window.t('vip')}
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded-t-lg bg-blue-500"></div>
                                {window.t('accessible')}
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded-t-lg bg-orange-900 border border-orange-500"></div>
                                {window.t('reserved')}
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded-t-lg bg-gray-800 border border-gray-700 opacity-75"></div>
                                {window.t('hidden')}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-3">
                    <div className="card overflow-auto">
                        <div style={{ minWidth: '100%', minHeight: '600px', width: '100%', height: '100%', padding: '24px' }}>
                            <div className="relative mx-auto bg-gray-950 rounded-xl border border-gray-800" style={{ width: Math.max(bounds.width, 400) + 'px', height: Math.max(bounds.height, 400) + 100 + 'px' }}>
                                <div className="absolute top-4 left-1/2 -translate-x-1/2 w-2/3 h-12 border-2 border-dashed border-gray-700 rounded-t-[100px] flex items-center justify-center text-gray-500 font-bold uppercase tracking-widest">
                                    {window.t('scena_ekran')}
                                </div>
                                {sector.seats?.map(seat => {
                                    let bgClass = 'bg-gray-500 hover:bg-gray-400';
                                    if (!seat.is_active) bgClass = 'bg-gray-800 border border-gray-700 opacity-75 hover:opacity-100';
                                    else if (seat.type === 'vip') bgClass = 'bg-yellow-500 hover:bg-yellow-400';
                                    else if (seat.type === 'accessible') bgClass = 'bg-blue-500 hover:bg-blue-400';
                                    else if (seat.type === 'reserved') bgClass = 'bg-orange-900 border border-orange-500 hover:bg-orange-800';
                                    return (
                                        <button
                                            key={seat.id || `${seat.row}-${seat.number}`}
                                            onClick={() => toggleSeatType(seat)}
                                            className={`absolute w-8 h-8 rounded-t-lg shadow-md transition-all hover:scale-110 flex items-center justify-center text-[10px] font-bold text-white shadow-black/50 ${bgClass}`}
                                            style={{ left: `${seat.pos_x}px`, top: `${seat.pos_y + 80}px` }}
                                            title={`${seat.row}${seat.number} - ${!seat.is_active ? 'Hidden' : seat.type}`}
                                        >
                                            {seat.row}<span className='text-gray-900'>{seat.number}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PromoCodesList() {
    const [promoCodes, setPromoCodes] = useState([])
    const [loading, setLoading] = useState(true)

    // Add/Edit Promo Code State
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedPromoCode, setSelectedPromoCode] = useState(null)
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        name_en: '',
        description: '',
        description_en: '',
        is_percentage: true,
        value: 10,
        usage_limit: '',
        expires_at: '',
        is_active: true,
        user_id: '',
        max_tickets_per_user: ''
    })

    const [userSearchQuery, setUserSearchQuery] = useState('')
    const [userSearchResults, setUserSearchResults] = useState([])
    const [selectedUserDisplay, setSelectedUserDisplay] = useState('')
    // const [isSearchingUser, setIsSearchingUser] = useState(false)

    const currentLanguage = localStorage.getItem('app_lang')

    const handleSearchUser = async (query) => {
        setUserSearchQuery(query)
        if (query.length < 2) {
            setUserSearchResults([])
            return
        }
        // setIsSearchingUser(true)
        try {
            const res = await adminGetUsers({ search: query })
            setUserSearchResults(res.data.data)
        } catch (error) {
            console.error('Failed to search users')
        } finally {
            // setIsSearchingUser(false)
        }
    }

    const selectUser = (user) => {
        setFormData({ ...formData, user_id: user.id })
        setSelectedUserDisplay(`${user.name} (${user.email})`)
        setUserSearchQuery('')
        setUserSearchResults([])
    }

    const clearSelectedUser = () => {
        setFormData({ ...formData, user_id: '' })
        setSelectedUserDisplay('')
    }

    const openCreateModal = () => {
        setFormData({
            code: '',
            name: '',
            name_en: '',
            description: '',
            description_en: '',
            is_percentage: true,
            value: 10,
            usage_limit: '',
            expires_at: '',
            is_active: true,
            user_id: '',
            max_tickets_per_user: ''
        })
        setSelectedPromoCode(null)
        setUserSearchQuery('')
        setSelectedUserDisplay('')
        setIsModalOpen(true)
    }

    const openEditModal = (code) => {
        setFormData({
            code: code.code,
            name: code.name || '',
            name_en: code.name_en || '',
            description: code.description || '',
            description_en: code.description_en || '',
            is_percentage: code.is_percentage,
            value: code.value,
            usage_limit: code.usage_limit || '',
            expires_at: code.expires_at ? code.expires_at.slice(0, 16) : '',
            is_active: code.is_active,
            user_id: code.user_id || '',
            max_tickets_per_user: code.max_tickets_per_user || ''
        })
        setSelectedPromoCode(code)
        if (code.user) {
            setSelectedUserDisplay(`${code.user.name} (${code.user.email})`)
        } else {
            setSelectedUserDisplay('')
        }
        setUserSearchQuery('')
        setIsModalOpen(true)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            const payload = {
                ...formData,
                value: parseFloat(formData.value),
                usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
                expires_at: formData.expires_at || null,
                user_id: formData.user_id || null,
                max_tickets_per_user: formData.max_tickets_per_user ? parseInt(formData.max_tickets_per_user) : null
            }

            if (selectedPromoCode) {
                await adminUpdatePromoCode(selectedPromoCode.id, payload)
                toast.success(window.t('uspeshna_aktualizatsiya'))
            } else {
                await adminCreatePromoCode(payload)
                toast.success(window.t('uspeshno_dobavyane'))
            }

            setIsModalOpen(false)
            loadPromoCodes()
        } catch (error) {
            toast.error(error.response?.data?.message || window.t('zapazvaneto_na_promotsionalniya_kod_ne_be_uspeshno'))
        }
    }

    const handleDelete = async (id) => {
        if (!confirm(`${window.t('sigrni_li_ste_che_iskate_da_iztriete_tozi_promotsionalen_kod')}`)) return
        try {
            await adminDeletePromoCode(id)
            toast.success(window.t('uspeshno_iztrivane'))
            loadPromoCodes()
        } catch (error) {
            toast.error(window.t('greshka_pri_iztrivane_na_dannite'))
        }
    }

    // PAGES
    const [page, setPage] = useState(1)
    const [lastPage, setLastPage] = useState(1)

    useEffect(() => {
        window.scrollTo(0, 0)
        loadPromoCodes()
    }, [page])

    const loadPromoCodes = async () => {
        try {
            const res = await adminGetPromoCodes({})

            const data = res.data

            setPromoCodes(data.data)
            setLastPage(data.last_page)
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        } finally {
            setLoading(false)
        }
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-white">{window.t('promo_kodove')}</h1>
                <button onClick={openCreateModal} className="btn-primary">
                    <FiPlus className="mr-2" /> {window.t('nov_promokod')}
                </button>
            </div>

            {loading ? (
                <div className="animate-pulse">{window.t('zarezhdane')}</div>
            ) : (
                <>
                    <div className="card overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-gray-800">
                                <tr>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('kod')}</th>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('otstapka')}</th>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('upotreba')}</th>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('validen_do')}</th>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('status')}</th>
                                    <th className="text-right p-4 text-gray-400 font-medium">{window.t('deystviya')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {promoCodes.map(code => (
                                    <tr key={code.id} className="border-t border-gray-800 hover:bg-gray-800/50">
                                        <td className="p-4">
                                            <div className="font-mono font-bold text-white">{code.code}</div>
                                            <div className="text-gray-400 text-sm">{currentLanguage === 'en' ? (code.name_en || code.name) : code.name}</div>
                                        </td>
                                        <td className="p-4 text-primary-400">
                                            {code.is_percentage ? `${code.value}%` : `€${code.value}`}
                                        </td>
                                        <td className="p-4 text-gray-400">
                                            {code.usage_count} / {code.usage_limit || '∞'}
                                        </td>
                                        <td className="p-4 text-gray-400">
                                            {code.expires_at
                                                ? formatDateTime(code.expires_at)
                                                : 'Never'}
                                        </td>
                                        <td className="p-4">
                                            <span className={`px-2 pt-[3px] pb-[5px] rounded-full text-xs ${code.is_active ? 'bg-green-500/20 text-green-800' : 'bg-red-500/20 text-red-800'}`}>
                                                {code.is_active ? `${window.t('aktiven')}` : `${window.t('neaktiven')}`}
                                            </span>
                                        </td>
                                        <td className="p-4 text-right space-x-2">
                                            <button onClick={() => openEditModal(code)} className="p-2 text-gray-400 hover:text-white transition-colors">
                                                <FiEdit2 size={18} />
                                            </button>
                                            <button onClick={() => handleDelete(code.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                                                <FiTrash2 size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-8 text-center">
                        <select
                            value={page}
                            onChange={(e) => setPage(Number(e.target.value))}
                            className="bg-gray-800 text-gray-300 border border-gray-700 rounded-lg px-3 py-2 text-sm outline-none cursor-pointer hover:text-white"
                        >
                            {Array.from({ length: lastPage }, (_, i) => i + 1).map((p) => (
                                <option key={p} value={p}>
                                    {p}
                                </option>
                            ))}
                        </select>
                    </div>
                </>
            )}

            {/* Add/Edit Promo Code Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl overflow-y-auto max-h-[90vh]">
                        <div className="flex justify-between items-center p-6 border-b border-gray-800">
                            <h2 className="text-xl font-bold text-white">
                                {selectedPromoCode ? `${window.t('redaktirane_na_promotsionalen_kod')}` : `${window.t('nov_promotsionalen_kod')}`}
                            </h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white">
                                <FiX size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label">{window.t('tselevi_kod')}</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.code}
                                        onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                        className="input uppercase font-mono"
                                        placeholder="SUMMER25"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label">{window.t('ime')} (BG)</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                                        className="input"
                                        placeholder={window.t('ime_na_balgarski')}
                                    />
                                </div>
                                <div>
                                    <label className="label">{window.t('ime')} (EN)</label>
                                    <input
                                        type="text"
                                        value={formData.name_en}
                                        onChange={e => setFormData({ ...formData, name_en: e.target.value })}
                                        className="input"
                                        placeholder={window.t('ime_na_angliyski')}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label">{window.t('opisanie')} (BG)</label>
                                    <input
                                        type="text"
                                        value={formData.description}
                                        onChange={e => setFormData({ ...formData, description: e.target.value })}
                                        className="input"
                                        placeholder={window.t('opisanie_na_balgarski')}
                                    />
                                </div>
                                <div>
                                    <label className="label">{window.t('opisanie')} (EN)</label>
                                    <input
                                        type="text"
                                        value={formData.description_en}
                                        onChange={e => setFormData({ ...formData, description_en: e.target.value })}
                                        className="input"
                                        placeholder={window.t('opisanie_na_angliyski')}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label">{window.t('vid_otstapka')}</label>
                                    <select
                                        value={formData.is_percentage ? 'true' : 'false'}
                                        onChange={e => setFormData({ ...formData, is_percentage: e.target.value === 'true' })}
                                        className="input"
                                    >
                                        <option value="true">{window.t('protsent')} (%)</option>
                                        <option value="false">{window.t('fiksirana_stoynost')} (EUR)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="label">{window.t('stoynost_na_otstapkata')}</label>
                                    <input
                                        type="number"
                                        required
                                        min="0.01"
                                        step="0.01"
                                        value={formData.value}
                                        onChange={e => setFormData({ ...formData, value: e.target.value })}
                                        className="input"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label">{window.t('limit_na_upotreba')} ({window.t('po_izbor')})</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={formData.usage_limit}
                                        onChange={e => setFormData({ ...formData, usage_limit: e.target.value })}
                                        className="input"
                                        placeholder={window.t('ne_popalvai_za_neogranicheno')}
                                    />
                                </div>
                                <div>
                                    <label className="label">{window.t('validna_do')} ({window.t('po_izbor')})</label>
                                    <input
                                        type="datetime-local"
                                        value={formData.expires_at}
                                        onChange={e => setFormData({ ...formData, expires_at: e.target.value })}
                                        className="input"
                                    />
                                </div>
                            </div>

                            <div className="pt-2">
                                <hr className="border-gray-800 mb-4" />
                                <h3 className="text-sm font-medium text-primary-400 mb-4">{window.t('ogranicheniya_spetsifichni_za_potrebitelya')}</h3>
                            </div>

                            <div className="grid grid-cols-2 gap-4 bg-gray-800/20 p-4 rounded-xl border border-gray-800">
                                <div>
                                    <label className="label">{window.t('ogranichavane_do_konkreten_potrebitel')}</label>
                                    {selectedUserDisplay ? (
                                        <div className="flex items-center justify-between input bg-gray-800 text-gray-300">
                                            <span className="truncate">{selectedUserDisplay}</span>
                                            <button type="button" onClick={clearSelectedUser} className="text-gray-400 hover:text-red-500">
                                                <FiX />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={userSearchQuery}
                                                onChange={e => handleSearchUser(e.target.value)}
                                                className="input"
                                                placeholder={window.t('tarsene_po_imeyl')}
                                            />
                                            {userSearchResults.length > 0 && (
                                                <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                                                    {userSearchResults.map(user => (
                                                        <div
                                                            key={user.id}
                                                            onClick={() => selectUser(user)}
                                                            className="p-3 hover:bg-gray-700 cursor-pointer border-b border-gray-700/50 last:border-0"
                                                        >
                                                            <div className="text-white text-sm font-medium">{user.name}</div>
                                                            <div className="text-gray-400 text-xs">{user.email}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className={!formData.user_id ? 'opacity-50 pointer-events-none' : ''}>
                                    <label className="label">{window.t('maksimalen_broy_bileti_na_potrebitel')}</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={formData.max_tickets_per_user}
                                        onChange={e => setFormData({ ...formData, max_tickets_per_user: e.target.value })}
                                        className="input"
                                        placeholder={window.t('maksimalen_broy')}
                                    />
                                    <p className="text-xs text-gray-500 mt-1">{window.t('ostavete_prazno_za_neogranicheni_osrebyavaniya_ot_tozi_potrebitel')}</p>
                                </div>
                            </div>

                            <div className="pt-4 flex gap-6">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                                        className="form-checkbox text-primary-500 rounded bg-gray-800 border-gray-700"
                                    />
                                    <span className="text-gray-300">{window.t('kodat_e_aktiven')}</span>
                                </label>
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-gray-800">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="btn-secondary"
                                >
                                    {window.t('otkaz')}
                                </button>
                                <button type="submit" className="btn-primary">
                                    {selectedPromoCode ? `${window.t('obnovi')}` : `${window.t('sazdai')}`}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}

function HelpPageManager() {
    const [categories, setCategories] = useState([])
    const [messages, setMessages] = useState([])
    const [tags, setTags] = useState([])
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState('faqs')
    const [expandedCategory, setExpandedCategory] = useState(null)

    // Modals state
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false)
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [categoryName, setCategoryName] = useState('')
    const [categoryOrder, setCategoryOrder] = useState(0)

    const [isFaqModalOpen, setIsFaqModalOpen] = useState(false)
    const [selectedFaq, setSelectedFaq] = useState(null)
    const [faqForm, setFaqForm] = useState({ question: '', answer: '', faq_category_id: '' })

    // Contact Messages & Tags State
    const [selectedMessage, setSelectedMessage] = useState(null)
    const [isMessageModalOpen, setIsMessageModalOpen] = useState(false)

    const [isTagModalOpen, setIsTagModalOpen] = useState(false)
    const [selectedTag, setSelectedTag] = useState(null)
    const [tagForm, setTagForm] = useState({ name: '', color: '#6b7280' })

    useEffect(() => {
        loadData()
    }, [])

    const loadData = async () => {
        try {
            const [
                faqsRes,
                // messagesRes, 
                tagsRes
            ] = await Promise.all([
                adminGetFaqs(),
                // adminGetContactMessages({}),
                adminGetContactMessageTags()
            ])
            setCategories(faqsRes.data.data || faqsRes.data)
            // setMessages(messagesRes.data.data || messagesRes.data)
            setTags(tagsRes.data.data || tagsRes.data)
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        } finally {
            setLoading(false)
        }
    }

    // Get message pages
    const [page, setPage] = useState(1)
    const [lastPage, setLastPage] = useState(1)

    useEffect(() => {
        window.scrollTo(0, 0)
        loadMessages()
    }, [page])

    const loadMessages = async () => {
        try {
            const res = await adminGetContactMessages({ page: page })

            const data = res.data

            setMessages(data.data)
            setLastPage(data.last_page)
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        } finally {
            setLoading(false)
        }
    }

    // Category Methods
    const openCategoryModal = (cat = null) => {
        setSelectedCategory(cat)
        setCategoryName(cat ? cat.name : '')
        setCategoryOrder(cat ? (cat.display_order || 0) : 0)
        setIsCategoryModalOpen(true)
    }

    const handleCategorySubmit = async (e) => {
        e.preventDefault()
        try {
            const slug = categoryName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
            const payload = {
                name: categoryName,
                slug: slug || 'category',
                display_order: categoryOrder,
                is_active: true
            }

            if (selectedCategory) await adminUpdateFaqCategory(selectedCategory.id, payload)
            else await adminCreateFaqCategory(payload)
            toast.success(selectedCategory ? window.t('uspeshna_aktualizatsiya') : window.t('uspeshno_dobavyane'))
            setIsCategoryModalOpen(false)
            loadData()
        } catch (err) {
            toast.error(window.t('zapazvaneto_na_kategoriyata_ne_be_uspeshno'))
        }
    }

    const handleDeleteCategory = async (id, e) => {
        e.stopPropagation()
        if (!confirm(`${window.t('da_se_iztrie_li_tazi_kategoriya_i_vsichkite_ѝ_faqs')}`)) return
        try {
            await adminDeleteFaqCategory(id)
            toast.success(window.t('uspeshno_iztrivane'))
            loadData()
        } catch {
            toast.error(window.t('iztrivaneto_na_kategoriyata_ne_be_uspeshno'))
        }
    }

    // FAQ Methods
    const openFaqModal = (cat, faq = null) => {
        setSelectedFaq(faq)
        setFaqForm({
            question: faq ? faq.question : '',
            answer: faq ? faq.answer : '',
            faq_category_id: cat.id
        })
        setIsFaqModalOpen(true)
    }

    const handleFaqSubmit = async (e) => {
        e.preventDefault()
        try {
            if (selectedFaq) await adminUpdateFaq(selectedFaq.id, faqForm)
            else await adminCreateFaq(faqForm)
            toast.success(selectedFaq ? window.t('uspeshna_aktualizatsiya') : window.t('uspeshno_dobavyane'))
            setIsFaqModalOpen(false)
            loadData()
        } catch (err) {
            toast.error(window.t('zapazvaneto_ne_be_uspeshno'))
        }
    }

    const handleDeleteFaq = async (id) => {
        if (!confirm(`${window.t('iztriy_tozi_faq')}`)) return
        try {
            await adminDeleteFaq(id)
            toast.success(window.t('uspeshno_iztrivane'))
            loadData()
        } catch {
            toast.error(window.t('greshka_pri_iztrivane_na_dannite'))
        }
    }

    // Message Methods
    const openMessageModal = async (msg) => {
        setSelectedMessage(msg)
        setIsMessageModalOpen(true)
        if (!msg.is_read) {
            try {
                await adminGetContactMessage(msg.id)
                loadData()
            } catch (err) { }
        }
    }

    const handleDeleteMessage = async (id) => {
        if (!confirm(`${window.t('da_se_iztrie_li_tova_sabshchenie')}`)) return
        try {
            await adminDeleteContactMessage(id)
            toast.success(window.t('uspeshno_iztrivane'))
            if (isMessageModalOpen) setIsMessageModalOpen(false)
            loadData()
        } catch {
            toast.error(window.t('greshka_pri_iztrivane_na_dannite'))
        }
    }

    const handleUpdateMessageTag = async (msgId, tagId) => {
        try {
            await adminUpdateContactMessage(msgId, { message_tag_id: tagId || null })
            toast.success(window.t('uspeshna_aktualizatsiya'))
            loadData()

            // if modal is open update local state
            if (selectedMessage && selectedMessage.id === msgId) {
                const updatedTag = tags.find(t => t.id === parseInt(tagId))
                setSelectedMessage({ ...selectedMessage, tag: updatedTag || null, message_tag_id: tagId || null })
            }
        } catch (err) {
            toast.error(window.t('greshka_pri_aktualizirane_na_dannite'))
        }
    }

    // Tags Methods
    const openTagModal = (tag = null) => {
        setSelectedTag(tag)
        setTagForm({ name: tag ? tag.name : '', color: tag ? tag.color : '#6b7280' })
        setIsTagModalOpen(true)
    }

    const handleTagSubmit = async (e) => {
        e.preventDefault()
        try {
            if (selectedTag) await adminUpdateContactMessageTag(selectedTag.id, tagForm)
            else await adminCreateContactMessageTag(tagForm)
            toast.success(selectedTag ? window.t('uspeshna_aktualizatsiya') : window.t('uspeshno_dobavyane'))
            setIsTagModalOpen(false)
            loadData()
        } catch (err) {
            toast.error(window.t('zapazvaneto_na_etiketa_ne_be_uspeshno'))
        }
    }

    const handleDeleteTag = async (id) => {
        if (!confirm(`${window.t('da_se_iztrie_li_tozi_etiket')}`)) return
        try {
            await adminDeleteContactMessageTag(id)
            toast.success(window.t('uspeshno_iztrivane'))
            loadData()
        } catch {
            toast.error(window.t('greshka_pri_iztrivane_na_dannite'))
        }
    }

    if (loading) return <div className="animate-pulse">{window.t('zarezhdane')}</div>

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-white">{window.t('pomoshtna_stranitsa_upravlenie')}</h1>
                <div className="flex items-center gap-3">
                    {activeTab === 'faqs' && (
                        <button onClick={() => openCategoryModal()} className="btn-primary">
                            <FiPlus className="mr-2" /> {window.t('kategoriya')}
                        </button>
                    )}
                    {activeTab === 'tags' && (
                        <button onClick={() => openTagModal()} className="btn-primary">
                            <FiPlus className="mr-2" /> {window.t('etiket')}
                        </button>
                    )}
                    {activeTab === 'messages' && (
                        <button onClick={loadData} className="btn-secondary">
                            <FiRefreshCw className="mr-2" /> {window.t('obnovi')}
                        </button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-4 mb-6">
                <button
                    onClick={() => setActiveTab('faqs')}
                    className={`px-4 py-2 rounded-lg transition-all ${activeTab === 'faqs' ? 'bg-primary-500 text-gray-900' : 'bg-gray-800 text-gray-400'
                        }`}
                >
                    FAQs ({categories.reduce((acc, cat) => acc + (cat.faqs?.length || 0), 0)})
                </button>
                <button
                    onClick={() => setActiveTab('messages')}
                    className={`px-4 py-2 rounded-lg transition-all ${activeTab === 'messages' ? 'bg-primary-500 text-gray-900' : 'bg-gray-800 text-gray-400'
                        }`}
                >
                    {window.t('sabshcheniya_kontakti')} ({messages.length})
                </button>
                <button
                    onClick={() => setActiveTab('tags')}
                    className={`px-4 py-2 rounded-lg transition-all ${activeTab === 'tags' ? 'bg-primary-500 text-gray-900' : 'bg-gray-800 text-gray-400'
                        }`}
                >
                    {window.t('sabshcheniya_etiketi')} ({tags.length})
                </button>
            </div>

            {activeTab === 'faqs' && (
                <div className="space-y-4">
                    {categories.map(category => (
                        <div key={category.id} className="card">
                            <div
                                onClick={() => setExpandedCategory(
                                    expandedCategory === category.id ? null : category.id
                                )}
                                className="w-full p-4 flex justify-between items-center text-left cursor-pointer hover:bg-gray-800/50 transition-colors"
                            >
                                <div>
                                    <h3 className="font-bold text-white">{category.name}</h3>
                                    <p className="text-gray-400 text-sm">{category.faqs?.length || 0} FAQs</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button onClick={(e) => { e.stopPropagation(); openCategoryModal(category) }} className="p-2 text-gray-400 hover:text-white transition-colors">
                                        <FiEdit2 />
                                    </button>
                                    <button onClick={(e) => handleDeleteCategory(category.id, e)} className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                                        <FiTrash2 />
                                    </button>
                                    <div className="p-2">
                                        {expandedCategory === category.id ? (
                                            <FiChevronUp className="text-gray-400" />
                                        ) : (
                                            <FiChevronDown className="text-gray-400" />
                                        )}
                                    </div>
                                </div>
                            </div>
                            {expandedCategory === category.id && (
                                <div className="border-t border-gray-800 p-4 space-y-4">
                                    <div className="flex justify-end">
                                        <button onClick={() => openFaqModal(category)} className="btn-secondary text-sm">
                                            <FiPlus className="mr-2" /> {window.t('dobavi_faq')}
                                        </button>
                                    </div>
                                    <div className="space-y-3">
                                        {category.faqs?.map(faq => (
                                            <div key={faq.id} className="flex justify-between items-start p-3 bg-gray-800/50 rounded-lg">
                                                <div className="flex-1">
                                                    <p className="text-white font-medium">{faq.question}</p>
                                                    <p className="text-gray-400 text-sm mt-1">{faq.answer}</p>
                                                </div>
                                                <div className="flex gap-2 ml-4">
                                                    <button onClick={() => openFaqModal(category, faq)} className="text-gray-400 hover:text-white p-2">
                                                        <FiEdit2 />
                                                    </button>
                                                    <button onClick={() => handleDeleteFaq(faq.id)} className="text-gray-400 hover:text-red-500 p-2">
                                                        <FiTrash2 />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {activeTab === 'messages' && (
                <>
                    <div className="card overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-gray-800">
                                <tr>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('ot')}</th>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('zaglavie')}</th>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('data')}</th>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('etiket')}</th>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('status')}</th>
                                    <th className="text-right p-4 text-gray-400 font-medium">{window.t('deystviya')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {messages.map(msg => {
                                    const tagColor = msg.tag ? msg.tag.color : null;
                                    return (
                                        <tr
                                            key={msg.id}
                                            className="border-t border-gray-800 hover:bg-gray-800/50 cursor-pointer transition-colors"
                                            style={{
                                                borderLeft: tagColor ? `4px solid ${tagColor}` : '4px solid transparent',
                                                backgroundColor: tagColor ? `${tagColor}11` : 'transparent' // add 11 parameter for transparent 10% opacity
                                            }}
                                            onClick={() => openMessageModal(msg)}
                                        >
                                            <td className="p-4">
                                                <div className="font-medium text-white">{msg.name}</div>
                                                <div className="text-gray-400 text-sm">{msg.email}</div>
                                            </td>
                                            <td className="p-4 text-gray-300">{msg.subject}</td>
                                            <td className="p-4 text-gray-400">
                                                {formatDateTime(msg.created_at)}
                                            </td>
                                            <td className="p-4" onClick={(e) => e.stopPropagation()}>
                                                <select
                                                    value={msg.message_tag_id || ''}
                                                    onChange={(e) => handleUpdateMessageTag(msg.id, e.target.value)}
                                                    className="input py-1 px-2 text-xs"
                                                >
                                                    <option value="">{window.t('nyama_etiketi')}</option>
                                                    {tags.map(t => (
                                                        <option key={t.id} value={t.id}>{t.name}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="p-4">
                                                <span className={`px-2 py-1 rounded-full text-xs ${msg.is_read ? 'bg-gray-500/20 text-gray-800' : 'bg-green-500/20 text-green-800'
                                                    }`}>
                                                    {msg.is_read ? window.t('procheteno') : window.t('novo')}
                                                </span>
                                            </td>
                                            <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    onClick={() => handleDeleteMessage(msg.id)}
                                                    className="text-red-400 hover:text-red-300 ml-2"
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-8 text-center">
                        <select
                            value={page}
                            onChange={(e) => setPage(Number(e.target.value))}
                            className="bg-gray-800 text-gray-300 border border-gray-700 rounded-lg px-3 py-2 text-sm outline-none cursor-pointer hover:text-white"
                        >
                            {Array.from({ length: lastPage }, (_, i) => i + 1).map((p) => (
                                <option key={p} value={p}>
                                    {p}
                                </option>
                            ))}
                        </select>
                    </div>
                </>
            )}

            {activeTab === 'tags' && (
                <div className="card overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-800">
                            <tr>
                                <th className="text-left p-4 text-gray-400 font-medium">{window.t('tsvyat')}</th>
                                <th className="text-left p-4 text-gray-400 font-medium">{window.t('ime')}</th>
                                <th className="text-right p-4 text-gray-400 font-medium">{window.t('deystviya')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tags.map(tag => (
                                <tr key={tag.id} className="border-t border-gray-800 hover:bg-gray-800/50">
                                    <td className="p-4">
                                        <div className="w-6 h-6 rounded-full" style={{ backgroundColor: tag.color || '#6b7280' }}></div>
                                    </td>
                                    <td className="p-4 text-white font-medium">{tag.name}</td>
                                    <td className="p-4 text-right flex justify-end gap-2">
                                        <button onClick={() => openTagModal(tag)} className="text-gray-400 hover:text-white p-2">
                                            <FiEdit2 />
                                        </button>
                                        <button onClick={() => handleDeleteTag(tag.id)} className="text-red-400 hover:text-red-300 p-2">
                                            <FiTrash2 />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create/Edit Category Modal */}
            {isCategoryModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
                        <div className="flex justify-between items-center p-6 border-b border-gray-800">
                            <h2 className="text-xl font-bold text-white">
                                {selectedCategory ? window.t('redaktirane_na_kategoriya') : window.t('nova_kategoriya')}
                            </h2>
                            <button onClick={() => setIsCategoryModalOpen(false)} className="text-gray-400 hover:text-white">
                                <FiX size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleCategorySubmit} className="p-6 space-y-4">
                            <div>
                                <label className="label">{window.t('ime_na_kategoriya')}</label>
                                <input
                                    type="text"
                                    required
                                    value={categoryName}
                                    onChange={e => setCategoryName(e.target.value)}
                                    className="input"
                                    placeholder={window.t('napr_osnovni_vaprosi')}
                                />
                            </div>
                            <div>
                                <label className="label">{window.t('podredba')} ({window.t('po_izbor')})</label>
                                <input
                                    type="number"
                                    value={categoryOrder}
                                    onChange={e => setCategoryOrder(Number(e.target.value))}
                                    className="input"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-gray-800">
                                <button type="button" onClick={() => setIsCategoryModalOpen(false)} className="btn-secondary">
                                    {window.t('otkaz')}
                                </button>
                                <button type="submit" className="btn-primary">
                                    {selectedCategory ? `${window.t('obnovi')}` : `${window.t('sazdai')}`}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create/Edit FAQ Modal */}
            {isFaqModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl overflow-y-auto max-h-[90vh]">
                        <div className="flex justify-between items-center p-6 border-b border-gray-800">
                            <h2 className="text-xl font-bold text-white">
                                {selectedFaq ? 'Edit FAQ' : 'Add New FAQ'}
                            </h2>
                            <button onClick={() => setIsFaqModalOpen(false)} className="text-gray-400 hover:text-white">
                                <FiX size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleFaqSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="label">{window.t('vapur')}</label>
                                <input
                                    type="text"
                                    required
                                    value={faqForm.question}
                                    onChange={e => setFaqForm({ ...faqForm, question: e.target.value })}
                                    className="input"
                                    placeholder={window.t('napr_kak_da_kupya_bilet')}
                                />
                            </div>
                            <div>
                                <label className="label">{window.t('otgovor')}</label>
                                <textarea
                                    required
                                    value={faqForm.answer}
                                    onChange={e => setFaqForm({ ...faqForm, answer: e.target.value })}
                                    className="input min-h-[120px]"
                                    placeholder={window.t('napishete_otgovora_tuk')}
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-gray-800">
                                <button type="button" onClick={() => setIsFaqModalOpen(false)} className="btn-secondary">
                                    {window.t('otkaz')}
                                </button>
                                <button type="submit" className="btn-primary">
                                    {selectedFaq ? `${window.t('obnovi')}` : `${window.t('sazdai')}`}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Message Viewer Modal */}
            {isMessageModalOpen && selectedMessage && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl">
                        <div className="flex justify-between items-center p-6 border-b border-gray-800">
                            <h2 className="text-xl font-bold text-white">{window.t('kontaktno_sabshchenie')}</h2>
                            <button onClick={() => setIsMessageModalOpen(false)} className="text-gray-400 hover:text-white">
                                <FiX size={24} />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="bg-gray-800/50 p-4 rounded-xl space-y-2">
                                <div className="flex items-center justify-between">
                                    <div className="text-white font-medium text-lg">{selectedMessage.name}</div>
                                    <div className="text-xs text-gray-400">{formatDateTime(selectedMessage.created_at)}</div>
                                </div>
                                <div className="text-primary-400 text-sm flex items-center gap-2">
                                    <FiMail /> <a href={`mailto:${selectedMessage.email}`} className="hover:underline">{selectedMessage.email}</a>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <div className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-1">{window.t('zaglavie')}</div>
                                    <div className="text-white text-lg font-bold">{selectedMessage.subject}</div>
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-1">{window.t('sabshchenie')}</div>
                                    <div className="text-gray-300 leading-relaxed bg-gray-950 p-4 rounded-xl border border-gray-800 whitespace-pre-wrap">
                                        {selectedMessage.message}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-between items-center pt-6 border-t border-gray-800">
                                <div className="flex items-center gap-3">
                                    <span className="text-sm text-gray-400">{window.t('etiket')}:</span>
                                    <select
                                        value={selectedMessage.message_tag_id || ''}
                                        onChange={(e) => handleUpdateMessageTag(selectedMessage.id, e.target.value)}
                                        className="input py-2 pl-3 pr-8 w-48"
                                    >
                                        <option value="">{window.t('nyama_etiketi')}</option>
                                        {tags.map(t => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </select>
                                    {selectedMessage.tag && (
                                        <div className="w-4 h-4 rounded-full ml-1" style={{ backgroundColor: selectedMessage.tag.color }}></div>
                                    )}
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => handleDeleteMessage(selectedMessage.id)}
                                        className="btn-secondary text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                    >
                                        <FiTrash2 className="mr-2" /> {window.t('iztriy')}
                                    </button>
                                    <a
                                        href={`mailto:${selectedMessage.email}?subject=Re: ${encodeURIComponent(selectedMessage.subject)}&body=${encodeURIComponent('\n\n\n-------------------------\nOn ' + formatDateTime(selectedMessage.created_at) + ' ' + selectedMessage.name + ' wrote:\n' + selectedMessage.message)}`}
                                        className="btn-primary"
                                    >
                                        <FiMail className="mr-2" /> {window.t('otgovori')}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create/Edit Tag Modal */}
            {isTagModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl">
                        <div className="flex justify-between items-center p-6 border-b border-gray-800">
                            <h2 className="text-xl font-bold text-white">
                                {selectedTag ? window.t('redaktirane_na_etiket') : window.t('nov_etiket')}
                            </h2>
                            <button onClick={() => setIsTagModalOpen(false)} className="text-gray-400 hover:text-white">
                                <FiX size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleTagSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="label">{window.t('ime_na_etiket')}</label>
                                <input
                                    type="text"
                                    required
                                    value={tagForm.name}
                                    onChange={e => setTagForm({ ...tagForm, name: e.target.value })}
                                    className="input"
                                    placeholder={window.t('napr_vazhno')}
                                />
                            </div>
                            <div>
                                <label className="label">{window.t('tsvyat_hex')} ({window.t('po_izbor')})</label>
                                <div className="flex gap-3">
                                    <input
                                        type="color"
                                        value={tagForm.color}
                                        onChange={e => setTagForm({ ...tagForm, color: e.target.value })}
                                        className="h-10 w-10 rounded border-0 cursor-pointer"
                                    />
                                    <input
                                        type="text"
                                        value={tagForm.color}
                                        onChange={e => setTagForm({ ...tagForm, color: e.target.value })}
                                        className="input flex-1"
                                        placeholder="#000000"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-gray-800">
                                <button type="button" onClick={() => setIsTagModalOpen(false)} className="btn-secondary">
                                    {window.t('otkaz')}
                                </button>
                                <button type="submit" className="btn-primary">
                                    {selectedTag ? `${window.t('obnovi')}` : `${window.t('sazdai')}`}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}

function PurchasesList() {
    const [orders, setOrders] = useState([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [sortBy, setSortBy] = useState('created_at')
    const [sortDir, setSortDir] = useState('desc')

    const [page, setPage] = useState(1)
    const [lastPage, setLastPage] = useState(1)

    useEffect(() => {
        window.scrollTo(0, 0)
        loadOrders()
    }, [statusFilter, sortBy, sortDir, page])

    const loadOrders = async (params = {}) => {
        try {
            setLoading(true)
            const res = await adminGetOrders({
                search,
                status: statusFilter,
                sort_by: sortBy,
                sort_dir: sortDir,
                page: page,
                ...params
            })

            setOrders(res.data.data || res.data)
            setLastPage(res.data.last_page)
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = (e) => {
        e.preventDefault()
        loadOrders()
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'paid':
            case 'completed': return 'bg-green-500/20 text-green-800'
            case 'pending': return 'bg-yellow-500/20 text-yellow-800'
            case 'cancelled': return 'bg-red-500/20 text-red-800'
            default: return 'bg-gray-500/20 text-gray-800'
        }
    }

    const getStatusText = (status) => {
        switch (status) {
            case 'paid': return `${window.t('platen')}`
            case 'completed': return `${window.t('platen')}`
            case 'pending': return `${window.t('izchakva')}`
            case 'cancelled': return `${window.t('otkazan')}`
            default: return status
        }
    }

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
        } else {
            setSortBy(column)
            setSortDir('asc')
        }
    }

    const renderSortIcon = (column) => {
        if (sortBy !== column) return null
        return sortDir === 'asc' ? <FiChevronUp className="inline ml-1" /> : <FiChevronDown className="inline ml-1" />
    }

    return (
        <div>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <h1 className="text-2xl font-bold text-white">{window.t('pokupki')}</h1>
                <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                    <form onSubmit={handleSearch} className="flex gap-2 w-full md:w-auto">
                        <input
                            type="text"
                            placeholder={window.t('tarsene_po_imeyl_ili_id')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="input md:w-64"
                        />
                        <button type="submit" className="btn-primary">{window.t('tarsi')}</button>
                    </form>
                    <div className="flex gap-2">
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="input w-full md:w-auto"
                        >
                            <option value="">{window.t('vsichki')}</option>
                            <option value="paid">{window.t('plateni')}</option>
                            <option value="pending">{window.t('izchakvashti')}</option>
                            <option value="cancelled">{window.t('otkazani')}</option>
                        </select>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="animate-pulse">{window.t('zarezhdane')}</div>
            ) : (
                <>
                    <div className="card overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-800">
                                    <tr>
                                        <th className="text-left p-4 text-gray-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('id')}>
                                            ID {renderSortIcon('id')}
                                        </th>
                                        <th className="text-left p-4 text-gray-400 font-medium">{window.t('klient')}</th>
                                        <th className="text-left p-4 text-gray-400 font-medium">{window.t('sabitie')}</th>
                                        <th className="text-left p-4 text-gray-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('total_amount')}>
                                            {window.t('stoynost')} {renderSortIcon('total_amount')}
                                        </th>
                                        <th className="text-left p-4 text-gray-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('status')}>
                                            {window.t('status')} {renderSortIcon('status')}
                                        </th>
                                        <th className="text-left p-4 text-gray-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('created_at')}>
                                            {window.t('data')} {renderSortIcon('created_at')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.length > 0 ? orders.map(order => (
                                        <tr key={order.id} className="border-t border-gray-800 hover:bg-gray-800/50">
                                            <td className="p-4">
                                                <span className="font-mono text-white">{order.id}</span>
                                            </td>
                                            <td className="p-4">
                                                <div className="font-medium text-white">{order.user?.name || 'Unknown'}</div>
                                                <div className="text-gray-400 text-sm">{order.user?.email || 'N/A'}</div>
                                            </td>
                                            <td className="p-4 text-gray-300">
                                                {order.performance?.event?.title || 'N/A'}
                                            </td>
                                            <td className="p-4 text-primary-400 font-medium">
                                                €{parseFloat(order.total_amount || 0).toFixed(2)}
                                            </td>
                                            <td className="p-4">
                                                <span className={`px-2 py-1 rounded-full text-xs capitalize ${getStatusColor(order.status)}`}>
                                                    {getStatusText(order.status)}
                                                </span>
                                            </td>
                                            <td className="p-4 text-gray-400">
                                                {formatDateTime(order.created_at)}
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="6" className="p-8 text-center text-gray-400">
                                                {window.t('ne_sa_namereni_porachki_koito_da_otgovaryat_na_izbranite_kriterii')}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="mt-8 text-center">
                        <select
                            value={page}
                            onChange={(e) => setPage(Number(e.target.value))}
                            className="bg-gray-800 text-gray-300 border border-gray-700 rounded-lg px-3 py-2 text-sm outline-none cursor-pointer hover:text-white"
                        >
                            {Array.from({ length: lastPage }, (_, i) => i + 1).map((p) => (
                                <option key={p} value={p}>
                                    {p}
                                </option>
                            ))}
                        </select>
                    </div>
                </>
            )}
        </div>
    )
}

function UsersList() {
    const [users, setUsers] = useState([])
    const [roles, setRoles] = useState([])
    const [stats, setStats] = useState({})
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')

    const [page, setPage] = useState(1)
    const [lastPage, setLastPage] = useState(1)

    useEffect(() => {
        loadRoles()
    }, [])

    useEffect(() => {
        window.scrollTo(0, 0)
        fetchUsers(page, search)
    }, [page])

    const loadRoles = async () => {
        try {
            const rolesRes = await adminGetRoles()
            setRoles(rolesRes.data?.data || rolesRes.data || [])
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        }
    }

    const fetchUsers = async (currentPage = page, currentSearch = search) => {
        try {
            setLoading(true)
            const res = await adminGetUsers({ page: currentPage, search: currentSearch })

            setUsers(res.data?.data || res.data || [])
            setStats(res.data?.stats || {})
            setLastPage(res.data?.meta?.last_page || 1)
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = (e) => {
        e.preventDefault()
        if (page !== 1) {
            setPage(1)
        } else {
            fetchUsers(1, search)
        }
    }

    const handleRoleChange = async (userId, newRole) => {
        try {
            await adminUpdateUser(userId, { role: newRole })
            toast.success(window.t('uspeshna_aktualizatsiya'))
            fetchUsers()
        } catch {
            toast.error(window.t('greshka_pri_aktualizirane_na_dannite'))
        }
    }

    const handleToggleActive = async (user) => {
        try {
            await adminToggleUserActive(user.id)
            toast.success(user.is_active ? window.t('potrebitelyat_e_blokiran') : window.t('potrebitelyat_e_otblokiran'))
            fetchUsers()
        } catch (error) {
            const msg = error.response?.data?.message || window.t('neuspeshna_promyana_na_statusa')
            toast.error(msg)
        }
    }

    const currentUser = useAuthStore(state => state.user)
    const currentUserID = currentUser?.id;

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-white">{window.t?.('potrebiteli') || 'Потребители'}</h1>
                <form onSubmit={handleSearch} className="flex gap-2">
                    <input
                        type="text"
                        placeholder={window.t('tarsene_po_ime_ili_imeyl')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="input w-64"
                    />
                    <button type="submit" className="btn-primary">{window.t('tarsi')}</button>
                </form>
            </div>

            {/* Stats */}
            {!loading && (
                <div className={`grid gap-4 mb-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-${Math.min(roles.length + 2, 6)}`}>
                    <div className="card p-4">
                        <div className="text-gray-400 text-sm">{window.t('obshto_potrebiteli')}</div>
                        <div className="text-2xl font-bold text-white">{stats.total_users || 0}</div>
                    </div>
                    <div className="card p-4">
                        <div className="text-gray-400 text-sm">{window.t('blokirani')}</div>
                        <div className="text-2xl font-bold text-red-400">{stats.blocked_users || 0}</div>
                    </div>
                    {roles.map((role, i) => {
                        const colors = ['text-primary-400', 'text-green-400', 'text-yellow-400', 'text-purple-400', 'text-blue-400', 'text-pink-400']
                        const colorClass = colors[i % colors.length]

                        return (
                            <div key={role.id} className="card p-4">
                                <div className="text-gray-400 text-sm">{role.display_name || role.name}</div>
                                <div className={`text-2xl font-bold ${colorClass}`}>{stats[role.name] || 0}</div>
                            </div>
                        )
                    })}
                </div>
            )}

            {loading ? (
                <div className="animate-pulse">{window.t('zarezhdane')}</div>
            ) : (
                <>
                    <div className="card overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-gray-800">
                                <tr>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('potrebitel')}</th>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t?.('imeyl') || 'Имейл'}</th>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('status')}</th>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t?.('rolya') || 'Роля'}</th>
                                    <th className="text-left p-4 text-gray-400 font-medium">{window.t('prisuedinil_se')}</th>
                                    <th className="text-right p-4 text-gray-400 font-medium">{window.t?.('deystviya')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(user => (
                                    <tr key={user.id} className={`border-t border-gray-800 hover:bg-gray-800/50 ${!user.is_active ? 'opacity-60' : ''}`}>
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.is_active ? 'bg-primary-500/20' : 'bg-red-500/20'}`}>
                                                    <span className={`font-bold ${user.is_active ? 'text-primary-400' : 'text-red-400'}`}>
                                                        {user.name?.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                                <span className="font-medium text-white">{user.name}</span>
                                            </div>
                                        </td>
                                        <td className="p-4 text-gray-400">{user.email}</td>
                                        <td className="p-4">
                                            <span className={`px-2 pt-[3px] pb-[5px] rounded-full text-xs ${user.is_active !== false ? 'bg-green-500/20 text-green-800' : 'bg-red-500/20 text-red-800'}`}>
                                                {user.is_active !== false ? window.t('aktiven') : window.t('blokiran')}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <select
                                                value={user.role?.name || 'customer'}
                                                onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                                className="bg-gray-800 text-white px-3 py-1 rounded-lg border border-gray-700 focus:border-primary-500 focus:outline-none"
                                            >
                                                {roles.map(role => (
                                                    <option key={role.id} value={role.name}>{role.display_name || role.name}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="p-4 text-gray-400">
                                            {formatDateTime ? formatDateTime(user.created_at) : user.created_at}
                                        </td>
                                        <td className="p-4 text-right">
                                            {currentUserID !== user.id &&
                                                <button
                                                    onClick={() => handleToggleActive(user)}
                                                    className={`p-2 rounded-lg transition-colors ${user.is_active !== false
                                                        ? 'text-red-400 hover:bg-red-500/10 hover:text-red-300'
                                                        : 'text-green-400 hover:bg-green-500/10 hover:text-green-300'
                                                        }`}
                                                    title={user.is_active !== false ? window.t('blokiray_potrebitel') : window.t('otblokiray_potrebitel')}
                                                >
                                                    {user.is_active !== false ? <FiLock /> : <FiUnlock />}
                                                </button>
                                            }
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-8 text-center">
                        <select
                            value={page}
                            onChange={(e) => setPage(Number(e.target.value))}
                            className="bg-gray-800 text-gray-300 border border-gray-700 rounded-lg px-3 py-2 text-sm outline-none cursor-pointer hover:text-white"
                        >
                            {Array.from({ length: lastPage }, (_, i) => i + 1).map((p) => (
                                <option key={p} value={p}>
                                    {p}
                                </option>
                            ))}
                        </select>
                    </div>

                </>
            )}
        </div>
    )
}

function ReportsPanel() {
    const [overview, setOverview] = useState({})
    const [revenueByHall, setRevenueByHall] = useState([])
    const [revenueByEvent, setRevenueByEvent] = useState([])
    const [revenueByCategory, setRevenueByCategory] = useState([])
    const [loading, setLoading] = useState(true)

    // Date filter state
    const [datePreset, setDatePreset] = useState('all')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [showCustomDates, setShowCustomDates] = useState(false)
    const [userType, setUserType] = useState('all')

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo(0, 0)
        }, 0);
    }, [])

    // Date presets
    const getPresetDates = (preset) => {
        const today = new Date()
        const formatDate = (d) => d.toISOString().split('T')[0]

        switch (preset) {
            case 'today':
                return { start: formatDate(today), end: formatDate(today) }
            case 'yesterday': {
                const yesterday = new Date(today)
                yesterday.setDate(yesterday.getDate() - 1)
                return { start: formatDate(yesterday), end: formatDate(yesterday) }
            }
            case 'last_7_days': {
                const weekAgo = new Date(today)
                weekAgo.setDate(weekAgo.getDate() - 7)
                return { start: formatDate(weekAgo), end: formatDate(today) }
            }
            case 'last_week': {
                const lastWeekEnd = new Date(today)
                lastWeekEnd.setDate(lastWeekEnd.getDate() - lastWeekEnd.getDay() - 1)
                const lastWeekStart = new Date(lastWeekEnd)
                lastWeekStart.setDate(lastWeekStart.getDate() - 6)
                return { start: formatDate(lastWeekStart), end: formatDate(lastWeekEnd) }
            }
            case 'this_month': {
                const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
                return { start: formatDate(monthStart), end: formatDate(today) }
            }
            case 'last_month': {
                const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
                const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0)
                return { start: formatDate(lastMonthStart), end: formatDate(lastMonthEnd) }
            }
            case 'ytd': {
                const yearStart = new Date(today.getFullYear(), 0, 1)
                return { start: formatDate(yearStart), end: formatDate(today) }
            }
            case 'last_year': {
                const lastYearStart = new Date(today.getFullYear() - 1, 0, 1)
                const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31)
                return { start: formatDate(lastYearStart), end: formatDate(lastYearEnd) }
            }
            case 'custom':
                return { start: startDate, end: endDate }
            default:
                return { start: '', end: '' }
        }
    }

    const presetOptions = [
        { value: 'all', label: window.t('prez_tsyaloto_vreme') },
        { value: 'today', label: window.t('dnes') },
        { value: 'yesterday', label: window.t('yesterday') },
        { value: 'last_7_days', label: window.t('poslednite_7_dni') },
        { value: 'last_week', label: window.t('minalata_sedmitsa') },
        { value: 'this_month', label: window.t('tozi_mesets') },
        { value: 'last_month', label: window.t('minaliyat_mesets') },
        { value: 'ytd', label: window.t('ot_nachaloto_na_godinata') },
        { value: 'last_year', label: window.t('minalata_godina') },
        { value: 'custom', label: window.t('custom') },
    ]

    useEffect(() => {
        loadReports()
    }, [])

    const loadReports = async (dates = null) => {
        try {
            setLoading(true)
            const params = {}

            if (dates && dates.start && dates.end) {
                params.start_date = dates.start
                params.end_date = dates.end
            }

            if (userType !== 'all') {
                params.user_type = userType
            }

            const [overviewRes, hallRes, eventRes, categoryRes] = await Promise.all([
                adminGetReportOverview(params),
                adminGetRevenueByHall(params),
                adminGetRevenueByEvent(params),
                adminGetRevenueByCategory(params)
            ])
            setOverview(overviewRes.data.data || {})
            setRevenueByHall(hallRes.data.data || [])
            setRevenueByEvent(eventRes.data.data || [])
            setRevenueByCategory(categoryRes.data.data || [])
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        } finally {
            setLoading(false)
        }
    }

    const handlePresetChange = (preset) => {
        setDatePreset(preset)
        setShowCustomDates(preset === 'custom')

        if (preset !== 'custom') {
            const dates = getPresetDates(preset)
            setStartDate(dates.start)
            setEndDate(dates.end)
            loadReports(dates)
        }
    }

    const handleUserTypeChange = (type) => {
        setUserType(type)

        const dates = datePreset !== 'custom'
            ? getPresetDates(datePreset)
            : { start: startDate, end: endDate };

        // Timeout to allow state to update before fetching
        setTimeout(() => loadReports(dates), 0)
    }

    const handleCustomDateApply = () => {
        if (startDate && endDate) {
            loadReports({ start: startDate, end: endDate })
        }
    }

    const formatCurrency = (amount) => `€${parseFloat(amount || 0).toFixed(2)}`

    const getMaxRevenue = (data) => Math.max(...data.map(d => parseFloat(d.total_revenue || 0)), 1)

    // const getSelectedLabel = () => {
    //     if (datePreset === 'custom' && startDate && endDate) {
    //         return `${startDate} to ${endDate}`
    //     }
    //     const opt = presetOptions.find(o => o.value === datePreset)
    //     return opt ? opt.label : 'През цялото време'
    // }

    return (
        <div className="space-y-8">
            {/* Header with Date Filter */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                <h1 className="text-2xl font-bold text-white">{window.t('spravki')}</h1>

                <div className="flex flex-wrap items-center gap-3">
                    {/* Preset Dropdown */}
                    <select
                        value={datePreset}
                        onChange={(e) => handlePresetChange(e.target.value)}
                        className="bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-primary-500 focus:outline-none"
                    >
                        {presetOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>

                    {/* User Type Dropdown */}
                    <select
                        value={userType}
                        onChange={(e) => handleUserTypeChange(e.target.value)}
                        className="bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-primary-500 focus:outline-none"
                    >
                        <option value="all">{window.t('vsichki')}</option>
                        <option value="cashiers_only">{window.t('samo_kasieri')}</option>
                        <option value="exclude_cashiers">{window.t('bez_kasieri')}</option>
                    </select>

                    {/* Custom Date Inputs */}
                    {showCustomDates && (
                        <div className="flex items-center gap-2">
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-primary-500 focus:outline-none"
                            />
                            <span className="text-gray-400">{window.t('do')}</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-primary-500 focus:outline-none"
                            />
                            <button
                                onClick={handleCustomDateApply}
                                disabled={!startDate || !endDate}
                                className="btn-primary px-4 py-2 disabled:opacity-50"
                            >
                                Apply
                            </button>
                        </div>
                    )}

                    {/* Current Selection Badge */}
                    {/* 
                    <div className="bg-primary-500/20 text-primary-400 px-3 py-1.5 rounded-full text-sm font-medium">
                        {getSelectedLabel()}
                    </div> 
                    */}
                </div>
            </div>

            {loading ? (
                <div className="animate-pulse text-gray-400">{window.t('zarezhdane_na_spravki')}</div>
            ) : (
                <>
                    {/* Overview Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="card p-6">
                            <div className="text-gray-400 text-sm">{window.t('obshto_prihodi')}</div>
                            <div className="text-3xl font-bold text-green-400">{formatCurrency(overview.total_revenue)}</div>
                        </div>
                        <div className="card p-6">
                            <div className="text-gray-400 text-sm">{window.t('plateni_porachki')}</div>
                            <div className="text-3xl font-bold text-primary-400">{overview.total_orders || 0}</div>
                        </div>
                        <div className="card p-6">
                            <div className="text-gray-400 text-sm">{window.t('sredna_stoynost')}</div>
                            <div className="text-3xl font-bold text-blue-400">{formatCurrency(overview.avg_order_value)}</div>
                        </div>
                        <div className="card p-6">
                            <div className="text-gray-400 text-sm">{window.t('ochakvashti_otkazani')}</div>
                            <div className="text-3xl font-bold text-yellow-400">
                                {overview.pending_orders || 0} / <span className="text-red-400">{overview.cancelled_orders || 0}</span>
                            </div>
                        </div>
                    </div>

                    {/* Revenue by Hall */}
                    <div className="card p-6">
                        <h2 className="text-xl font-semibold text-white mb-4">{window.t('prihodi_po_zali')}</h2>
                        {revenueByHall.length === 0 ? (
                            <p className="text-gray-400">{window.t('nyama_danni_za_izbraniya_perio')}</p>
                        ) : (
                            <div className="space-y-3">
                                {revenueByHall.map(hall => (
                                    <div key={hall.id} className="flex items-center gap-4">
                                        <div className="w-32 text-white font-medium truncate">{hall.name}</div>
                                        <div className="flex-1 bg-gray-800 rounded-full h-6 overflow-hidden">
                                            <div
                                                className="bg-gradient-to-r from-primary-500 to-blue-500 h-full rounded-full transition-all"
                                                style={{ width: `${(parseFloat(hall.total_revenue) / getMaxRevenue(revenueByHall)) * 100}%` }}
                                            />
                                        </div>
                                        <div className="w-24 text-right text-green-400 font-semibold">{formatCurrency(hall.total_revenue)}</div>
                                        <div className="w-20 text-right text-gray-400 text-sm">{hall.order_count} {window.t('porachki')}</div>
                                        <div className="w-24 text-right text-blue-400 text-sm">{window.t('sredno')}: {formatCurrency(hall.avg_revenue)}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Revenue by Category */}
                    <div className="card p-6">
                        <h2 className="text-xl font-semibold text-white mb-4">{window.t('prihodi_po_tip_sabitie')}</h2>
                        {revenueByCategory.length === 0 ? (
                            <p className="text-gray-400">{window.t('nyama_danni_za_izbraniya_perio')}</p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {revenueByCategory.map(cat => (
                                    <div key={cat.category} className="bg-gray-800 rounded-xl p-4">
                                        <div className="text-lg font-semibold text-white capitalize">{getCategoryLabel(cat.category)}</div>
                                        <div className="text-gray-400 text-sm mt-1">{cat.order_count} {window.t('porachki')} • {window.t('sredno')}: {formatCurrency(cat.avg_revenue)}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Revenue by Event */}
                    <div className="card p-6">
                        <h2 className="text-xl font-semibold text-white mb-4">{window.t('prihodi_po_sabitie')}</h2>
                        {revenueByEvent.length === 0 ? (
                            <p className="text-gray-400">{window.t('nyama_danni_za_izbraniya_perio')}</p>
                        ) : (
                            <table className="w-full">
                                <thead className="bg-gray-800">
                                    <tr>
                                        <th className="text-left p-3 text-gray-400">{window.t('sabitie')}</th>
                                        <th className="text-left p-3 text-gray-400">{window.t('kategoriya')}</th>
                                        <th className="text-right p-3 text-gray-400">{window.t('porachki')}</th>
                                        <th className="text-right p-3 text-gray-400">{window.t('prihodi')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {revenueByEvent.map(event => (
                                        <tr key={event.id} className="border-t border-gray-800">
                                            <td className="p-3 text-white font-medium">{event.title}</td>
                                            <td className="p-3 text-gray-400 capitalize">{getCategoryLabel(event.category)}</td>
                                            <td className="p-3 text-right text-primary-400">{event.order_count}</td>
                                            <td className="p-3 text-right text-green-400 font-semibold">{formatCurrency(event.total_revenue)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}

function EventPerformances() {
    const { eventSlug } = useParams()
    const navigate = useNavigate()
    const [performances, setPerformances] = useState([])
    const [halls, setHalls] = useState([])
    const [eventData, setEventData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedPerf, setSelectedPerf] = useState(null)
    const [formData, setFormData] = useState({
        start_time: '',
        end_time: '',
        base_price: '',
        available_seats: '',
        is_active: true,
    })

    const currentLanguage = localStorage.getItem('app_lang')

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo(0, 0)
        }, 0);
    }, [])

    useEffect(() => {
        loadData()
    }, [eventSlug])

    const loadData = async () => {
        try {
            setLoading(true)
            const eventRes = await adminGetEvent(eventSlug)
            const event = eventRes.data?.data ? eventRes.data.data : eventRes.data
            setEventData(event)

            const [perfRes, hallsRes] = await Promise.all([
                adminGetPerformances({ event_id: event.id }),
                adminGetHalls()
            ])
            setPerformances(perfRes.data.data || perfRes.data)
            setHalls(hallsRes.data.data || hallsRes.data)
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        } finally {
            setLoading(false)
        }
    }

    const openModal = (perf = null) => {
        if (perf) {
            setSelectedPerf(perf)
            // Create a valid datetime-local string (YYYY-MM-DDTHH:mm)
            const formatForInput = (dateStr) => {
                if (!dateStr) return ''
                const d = new Date(dateStr)
                // Need to offset timezone to get correct local time in input
                return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
            }

            setFormData({
                start_time: formatForInput(perf.start_time),
                end_time: perf.end_time ? formatForInput(perf.end_time) : '',
                base_price: perf.base_price,
                available_seats: perf.available_seats || '',
                is_active: perf.is_active
            })
        } else {
            setSelectedPerf(null)
            setFormData({
                start_time: '',
                end_time: '',
                base_price: '',
                available_seats: '',
                is_active: true
            })
        }
        setIsModalOpen(true)
    }

    const closeModal = () => setIsModalOpen(false)

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            const payload = {
                ...formData,
                event_id: eventData.id
            }
            if (!payload.end_time) delete payload.end_time
            if (!payload.available_seats) delete payload.available_seats

            if (selectedPerf) {
                await adminUpdatePerformance(selectedPerf.id, payload)
                toast.success(window.t('uspeshna_aktualizatsiya'))
            } else {
                await adminCreatePerformance(payload)
                toast.success(window.t('uspeshno_dobavyane'))
            }
            closeModal()
            loadData()
        } catch (error) {
            const msgs = error.response?.data?.errors
                ? Object.values(error.response.data.errors).flat().join('\n')
                : error.response?.data?.message || window.t('neuspesheн_zapis_na_predstavlenie')
            toast.error(msgs)
        }
    }

    const handleDelete = async (id) => {
        if (!window.confirm(`${window.t('sigrni_li_ste_che_iskate_da_iztriete_tova_predstavlenie')}`)) return;
        try {
            await adminDeletePerformance(id)
            toast.success(window.t('uspeshno_iztrivane'))
            loadData()
        } catch (error) {
            toast.error(error.response?.data?.message || window.t('greshka_pri_iztrivane_na_dannite'))
        }
    }

    return (
        <div>
            <div className="flex items-center mb-6 pb-4 border-b border-gray-800">
                <div className="flex items-center gap-4 flex-1">
                    <button
                        onClick={() => navigate('/admin/events')}
                        className="text-gray-400 hover:text-white transition-colors flex items-center shrink-0"
                    >
                        &larr; {window.t('obratno_kam_sabitiata')}
                    </button>
                    <span className="text-gray-700">|</span>
                    <h1 className="text-2xl font-bold text-white truncate">
                        {eventData ? (currentLanguage === 'en' ? (eventData.title_en || eventData.title) : eventData.title) : window.t('zarezhdane')}
                    </h1>
                    <span className="text-gray-500 font-medium whitespace-nowrap">
                        {window.t('upravlenie_na_predstavleniya')}
                    </span>
                </div>
                <button onClick={() => openModal()} className="btn-primary shrink-0 ml-4">
                    <FiPlus className="mr-2" /> {window.t('dobavi_predstavlenie')}
                </button>
            </div>

            {loading ? (
                <div className="animate-pulse text-gray-400">{window.t('zarezhdane')}</div>
            ) : (
                <div className="card overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-gray-800">
                            <tr>
                                <th className="p-4 text-gray-400 font-medium whitespace-nowrap">{window.t('nachalo')}</th>
                                <th className="p-4 text-gray-400 font-medium">{window.t('zala')}</th>
                                <th className="p-4 text-gray-400 font-medium">{window.t('bazova_tsena')}</th>
                                <th className="p-4 text-gray-400 font-medium">{window.t('status')}</th>
                                <th className="text-right p-4 text-gray-400 font-medium">{window.t('deystviya')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {performances.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-gray-400">
                                        {window.t('nyama_namereni_predstavleniya_za_tova_sabitie')}
                                    </td>
                                </tr>
                            ) : performances.map(perf => (
                                <tr key={perf.id} className="border-t border-gray-800 hover:bg-gray-800/50">
                                    <td className="p-4">
                                        <div className="font-medium text-white">{formatDateTime(perf.start_time)}</div>
                                        {perf.end_time && <div className="text-gray-400 text-sm">{window.t('do')} {formatDateTime(perf.end_time)}</div>}
                                    </td>
                                    <td className="p-4 text-gray-300">
                                        {perf.hall ? (currentLanguage === 'en' ? (perf.hall.name_en || perf.hall.name) : perf.hall.name) : '-----'}
                                    </td>
                                    <td className="p-4 text-primary-400 font-medium">€{perf.base_price}</td>
                                    <td className="p-4">
                                        <span className={`px-2 pt-[3px] pb-[5px] rounded-full text-xs ${perf.is_active
                                            ? 'bg-green-500/20 text-green-800'
                                            : 'bg-red-500/20 text-red-800'
                                            }`}>
                                            {perf.is_active ? window.t('aktiven') : window.t('neaktiven')}
                                        </span>
                                    </td>
                                    <td className="p-4 flex gap-3 justify-end items-center h-full">
                                        <button onClick={() => openModal(perf)} className="btn-secondary py-1 px-3 pt-[3px] pb-[5px] text-sm">
                                            {window.t('redaktirane')}
                                        </button>
                                        <button onClick={() => handleDelete(perf.id)} className="btn-secondary py-1 px-3 pt-[3px] pb-[5px] text-sm bg-red-500/20 text-red-800 hover:bg-red-300 ">
                                            {window.t('iztriy')}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 pt-20 overflow-y-auto">
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-lg my-8 relative">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-white">
                                {selectedPerf ? window.t('redaktirane_na_predstavlenie') : window.t('dobavyane_na_predstavlenie')}
                            </h2>
                            <button onClick={closeModal} className="text-gray-400 hover:text-white transition-colors">
                                <FiX className="w-6 h-6" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label">{window.t('nachalo')}</label>
                                    <input
                                        type="datetime-local"
                                        className="input"
                                        value={formData.start_time}
                                        onChange={e => setFormData({ ...formData, start_time: e.target.value })}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="label">{window.t('krai')} ({window.t('po_izbor')})</label>
                                    <input
                                        type="datetime-local"
                                        className="input"
                                        value={formData.end_time}
                                        onChange={e => setFormData({ ...formData, end_time: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="label">{window.t('bazova_tsena')} (EUR)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="input"
                                        value={formData.base_price}
                                        onChange={e => setFormData({ ...formData, base_price: e.target.value })}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="label">{window.t('nalichni_mesta')} ({window.t('po_izbor')})</label>
                                    <input
                                        type="number"
                                        className="input"
                                        placeholder={window.t('kapatsiteta_na_zalata')}
                                        value={formData.available_seats}
                                        onChange={e => setFormData({ ...formData, available_seats: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="flex gap-6 mt-6">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                                        className="form-checkbox text-primary-500 rounded bg-gray-800 border-gray-700 focus:ring-primary-500 focus:ring-offset-gray-900"
                                    />
                                    <span className="text-gray-300">{window.t('aktiven')}</span>
                                </label>
                            </div>
                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-800 mt-6">
                                <button type="button" onClick={closeModal} className="btn-secondary">{window.t('otkaz')}</button>
                                <button type="submit" className="btn-primary">
                                    {selectedPerf ? `${window.t('obnovi')}` : `${window.t('sazdai')}`}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}

function NewslettersList() {
    const [newsletters, setNewsletters] = useState([])
    const [loading, setLoading] = useState(true)
    const [selectedTopic, setSelectedTopic] = useState(null) // Controls the modal

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo(0, 0)
        }, 0);
    }, [])

    useEffect(() => {
        loadNewsletters()
    }, [])

    const loadNewsletters = async () => {
        try {
            const res = await adminGetNewsletters()
            setNewsletters(res.data.data || res.data)
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
        } finally {
            setLoading(false)
        }
    }

    const toggleStatus = async (nl) => {
        try {
            await adminUpdateNewsletter(nl.id, { is_active: !nl.is_active })
            toast.success(`${window.t('byuletinat_e')} ${nl.is_active ? `${window.t('spryan')}` : `${window.t('aktiviran')}`}`)
            loadNewsletters()
        } catch (error) {
            toast.error(window.t('greshka_pri_aktualizirane_na_dannite'))
        }
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-white">{window.t('byuletini')}</h1>
            </div>

            {loading ? (
                <div className="animate-pulse text-gray-400">{window.t('zarezhdane')}</div>
            ) : (
                <div className="card overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-800">
                            <tr>
                                <th className="text-left p-4 text-gray-400 font-medium">{window.t('tema')}</th>
                                <th className="text-left p-4 text-gray-400 font-medium">{window.t('status')}</th>
                                <th className="text-right p-4 text-gray-400 font-medium">{window.t('abonati')}</th>
                                <th className="text-right p-4 text-gray-400 font-medium">{window.t('deystviya')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {newsletters.map(nl => (
                                <tr key={nl.id} className="border-t border-gray-800 hover:bg-gray-800/50">
                                    <td className="p-4">
                                        <div className="font-medium text-white">{nl.name}</div>
                                        {nl.description && <div className="text-sm text-gray-400">{nl.description}</div>}
                                    </td>
                                    <td className="p-4">
                                        <span className={`px-2 pt-[3px] pb-[5px] rounded-full text-xs ${nl.is_active ? 'bg-green-500/20 text-green-800' : 'bg-red-500/20 text-red-800'}`}>
                                            {nl.is_active ? window.t('aktiven') : window.t('neaktiven')}
                                        </span>
                                    </td>
                                    <td className="p-4 text-right">
                                        <span className="text-white font-bold">{nl.subscriptions_count || 0}</span>
                                    </td>
                                    <td className="p-4 text-right flex justify-end gap-2">
                                        <button
                                            onClick={() => toggleStatus(nl)}
                                            className={`btn-outline px-3 pt-[3px] pb-[5px] text-sm flex items-center gap-2 ${nl.is_active ? 'hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/50 text-gray-300' : 'hover:bg-green-500/20 hover:text-green-400 hover:border-green-500/50 text-gray-300'}`}
                                        >
                                            {nl.is_active ? window.t('spiri') : window.t('pusni')}
                                        </button>
                                        <button
                                            onClick={() => setSelectedTopic(nl)}
                                            className="btn-outline px-3 pt-[3px] pb-[5px] text-sm flex items-center gap-2"
                                        >
                                            {/* <FiEye />  */}
                                            {window.t('vizh_abonatite')}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {newsletters.length === 0 && (
                        <div className="p-8 text-center text-gray-400">{window.t('nyama_namereni_byuletini')}</div>
                    )}
                </div>
            )}

            {/* Subscribers Modal */}
            {selectedTopic && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-2xl my-8 relative max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h2 className="text-xl font-bold text-white">
                                    {window.t('abonati')}: {selectedTopic.name}
                                </h2>
                                <p className="text-sm text-gray-400">{window.t('obshto')}: {selectedTopic.subscriptions_count || 0}</p>
                            </div>
                            <button onClick={() => setSelectedTopic(null)} className="text-gray-400 hover:text-white transition-colors">
                                <FiX className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="overflow-y-auto flex-1 pr-2">
                            {(!selectedTopic.subscriptions || selectedTopic.subscriptions.length === 0) ? (
                                <div className="p-8 text-center text-gray-400">{window.t('nyama_abonati_za_tazi_tema')}</div>
                            ) : (
                                <div className="space-y-3">
                                    {selectedTopic.subscriptions.map(sub => (
                                        <div key={sub.id} className="flex justify-between items-center p-4 bg-gray-800/50 rounded-lg">
                                            <div>
                                                <div className="text-white font-medium">{sub.user?.name || 'Неизвестен потребител'}</div>
                                                <div className="text-sm text-gray-400">{sub.user?.email || '—'}</div>
                                                {sub.user?.phone && <div className="text-sm text-gray-400">{sub.user.phone}</div>}
                                            </div>
                                            <div className="text-right text-sm text-gray-500">
                                                {window.t('aboniran_na')}<br />
                                                {new Date(sub.created_at).toLocaleDateString('bg-BG')}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default function AdminDashboard() {
    const user = useAuthStore(state => state.user)
    const isCashier = user?.role?.name === 'cashier'

    if (isCashier) {
        return (
            <div className="flex min-h-screen bg-gray-950">
                <main className="flex-1 w-full p-4 lg:p-8 pt-24">
                    <Routes>
                        <Route path="/" element={<Navigate to="/admin/cashier" replace />} />
                        <Route path="/cashier" element={<CashierDesk />} />
                        <Route path="*" element={<Navigate to="/admin/cashier" replace />} />
                    </Routes>
                </main>
            </div>
        )
    }

    return (
        <div className="flex min-h-screen">
            <AdminNav />
            <main className="flex-1 p-8 pt-24">
                <Routes>
                    <Route path="/" element={<DashboardStats />} />
                    <Route path="/cashier" element={<CashierDesk />} />
                    <Route path="/events" element={<EventsList />} />
                    <Route path="/events/:eventSlug/performances" element={<EventPerformances />} />
                    <Route path="/halls" element={<HallsList />} />
                    <Route path="/halls/:hallId/map" element={<HallMapEditor />} />
                    <Route path="/halls/:hallId/sectors/:sectorId/layout" element={<SectorLayoutEditor />} />
                    <Route path="/halls/:hallId/layout" element={<HallLayoutRedirect />} />
                    <Route path="/promo-codes" element={<PromoCodesList />} />
                    <Route path="/help" element={<HelpPageManager />} />
                    <Route path="/purchases" element={<PurchasesList />} />
                    <Route path="/users" element={<UsersList />} />
                    <Route path="/newsletters" element={<NewslettersList />} />
                    <Route path="/reports" element={<ReportsPanel />} />
                </Routes>
            </main>
        </div>
    )
}
